/**
 * Action Center — single unified Inbox (DEV-1201, POLICY_ACTION_CENTER.md).
 *
 * One priority-ranked list of every action item in the user's court, a Type
 * column, and a type-specific tray on row click (ActionItemTray). Tracking
 * lives in a separate "Sent" view. Flag-gated behind isActionCenterInboxEnabled.
 *
 * Phase 1: frontend join of the streams at render time (events from the
 * workflow store, rhythms from useApiRoleRhythmInstances, data from the row
 * array, milestones from the onboarding evaluator). The backend union
 * endpoint is a later phase per the design lock.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAccountSettings } from "../../Components/AccountSettings/AccountSettingsContext";
import EulaModal from "../../Components/EulaModal/EulaModal";
import { fetchCompletedItems, type CompletedActionItem } from "../../api/actionItemsCompleted";
import { useActionItemComposer } from "../../hooks/useActionItemComposer";
import { useActionItemSeenStore } from "../../state/actionItemSeenStore";
import {
  addOrUpdateWorkflow,
  getAllWorkflows,
  approveRequest,
  rejectRequest,
  applyApprovedWorkflowSideEffects,
  applyRejectedWorkflowSideEffects,
} from "../../workflow";
import type { WorkflowRequest } from "../../workflow";
import { acknowledgeCommunityGuidelines } from "../../api/communityGuidelines";
import CommunityStandardsModal from "../../Components/CommunityStandardsModal/CommunityStandardsModal";
import CommunityGuidelinesModal from "../../Components/CommunityGuidelinesModal/CommunityGuidelinesModal";
import TotpSetupModal from "../../Components/TotpSetup/TotpSetupModal";
import WelcomeScreenModal from "../../Components/WelcomeScreenModal/WelcomeScreenModal";
import { STANDARDS_CURRENT_VERSION } from "../../content/legal/standardsConstants";
import {
  completeRoleRhythmInstance,
  snoozeRoleRhythmInstance,
} from "../../api/roleRhythms";
import {
  transitionMiniProjectTaskStatus,
  addMiniProjectTaskEvidence,
  removeMiniProjectTaskEvidence,
} from "../../api/miniProjectTasks";
import { evaluateMilestones } from "../../Data/Onboarding/milestoneEvaluator";
import ActionItemTray, { type ActionItemTrayHandlers } from "./ActionItemTray";
/* DEV-1407 / DEV-1476 — the AC rail runs on the shared primitive now. */
import Rail from "../../Components/Rail/Rail";
import RailSearch from "../../Components/Rail/RailSearch";
import type { SelectableEntry } from "../../Data/Engagement/engagementEntry";
import { PILLAR_CSS_TOKEN } from "../../Data/Engagement/engagementEntry";
import { mediaSrc } from "../../api/mediaUrl";
import { patchGanttTaskProgress } from "../../api/ganttTasks";
import { invalidateAndRefetch } from "../../api/queryCache";
import { resolveEffectiveLogoUrl } from "../../api/communityMetadata";
import RoleDigestView from "../../Components/ProjectTeam/RoleDigestView";
import { resolveCommunityPath } from "../../Data/ProjectConfig/projectConfigOverrides";
import {
  actionItemScore,
  eventKind,
  rhythmToActionItem,
  gettingStartedToActionItems,
  promotionNoticeToActionItem,
  typeKeyFor,
  statusKey,
  groupActionItems,
  pillarForEntityType,
  ROLE_BASE_KEY,
  ROLE_OTHER_KEY,
  type ActionItem,
  type ActionKind,
  type ActionStatus,
  type RolePillar,
} from "./actionItems";
import styles from "./ActionCenterInbox.module.css";

interface Props {
  currentUserGuid: string;
  debugMode?: boolean;
  users?: Record<string, any>[];
  projects?: Record<string, any>[];
  communities?: Record<string, any>[];
  relationships?: Record<string, any>[];
  data?: any[];
  onProjectConfigChanged?: () => void;
  communityMemberships?: Record<string, any>[];
  projectAcl?: Record<string, any>[];
  /** SPEC_ROLE_ORGANIZED_INBOX §4a — role-sourced rail (useEngagementEntries). */
  allProjectHeroLookup?: Map<string, string>;
  communityRoleAssignments?: Record<string, any>[];
}

// DEV-1223: two top-level states — "To do" (open, in my court) and "Done"
// (finished history, lifecycle-threaded). Replaces the prior Inbox/Sent split
// + the open/handled action filter; the "Waiting" (outbound-awaiting) concept
// is cut (returns with redelegation, future work).
type View = "todo" | "done";
type SortKey = "priority" | "type" | "source" | "role" | "added" | "date" | "status";
// Read axis (unseen/seen) still applies within To do. Search covers "find a
// type"; what users filter by is state.
type ReadFilter = "all" | "unseen" | "seen";

const STATUS_CLASS: Record<ActionStatus, string> = {
  overdue: styles.stOverdue,
  due: styles.stDue,
  needs_response: styles.stNeeds,
  ongoing: styles.stOngoing,
  snoozed: styles.stSnoozed,
  resolved: styles.stResolved,
};

// SPEC_ROLE_ORGANIZED_INBOX §4a — entity-grouped rail.
// "role" = collapsible role/community sections (default); "flat" = the locked
// single priority list.
type GroupMode = "role" | "flat";
// Pillar → avatar-ring class. Container pillar (community/enabler) on group
// headers; community (white) renders as a neutral ring since white is invisible.
const PILLAR_CLASS: Record<RolePillar, string> = {
  enabler: styles.pillEnabler,
  community: styles.pillCommunity,
  project: styles.pillProject,
  problem: styles.pillProblem,
  solution: styles.pillSolution,
  base: styles.pillBase,
  other: styles.pillOther,
};
// Filled square role tile (the role's own pillar) — distinct from the ringed
// community avatar. SPEC §4a: square = pillar identity.
const FILL_CLASS: Record<RolePillar, string> = {
  enabler: styles.fillEnabler,
  community: styles.fillCommunity,
  project: styles.fillProject,
  problem: styles.fillProblem,
  solution: styles.fillSolution,
  base: styles.fillBase,
  other: styles.fillOther,
};

// Country code → flag emoji (mirrors the community pages' helper). Geo
// communities show their inherited country flag as the rail avatar (DEV-1368).
function countryFlag(code: string | null | undefined): string {
  if (!code || code.length < 2) return "";
  const upper = code.toUpperCase().trim();
  const cc = upper.indexOf("-") === 2 ? upper.slice(0, 2) : upper.length === 2 ? upper : "";
  if (!cc || !/^[A-Z]{2}$/.test(cc)) return "";
  return String.fromCodePoint(...Array.from(cc).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

// Does an action item belong to a given role engagement? Shared by the rail's
// role filter AND the per-role/community count badges so they never diverge
// (DEV-1368). Mirrors the §4a join: RACI tasks match by role slot, data by
// project, everything else by owning community + role label.
function itemMatchesEntry(it: ActionItem, ent: SelectableEntry): boolean {
  const cg = ent.groupCommunityGuid
    ? `community:${ent.groupCommunityGuid.toUpperCase()}`
    : null;
  if (!cg) return false;
  const d = ent.source.data as any;
  const projGuid = d?.projectGuid ? String(d.projectGuid).toUpperCase() : null;
  const entSlot = d?.roleSlotGuid ? String(d.roleSlotGuid).toUpperCase() : null;
  if (it.kind === "raci") {
    return !!entSlot && String(it.raciTask?.roleSlotGuid || "").toUpperCase() === entSlot;
  }
  // Rhythm tasks: match by the STABLE slot GUID when both sides have it — the
  // role NAME in sourceLabel breaks when the slot is renamed (Skip 2026-06-24:
  // "Secretary" vs the instance's stale "Rotary Club Secretary"). Falls through
  // to the community + label match for legacy/non-slot rhythms.
  if (it.kind === "rhythm" || it.kind === "standing") {
    const instSlot = (it.instance as any)?.roleSlotGuid
      ? String((it.instance as any).roleSlotGuid).toUpperCase()
      : null;
    if (instSlot && entSlot) return instSlot === entSlot;
  }
  if (it.roleGroupKey !== cg) return false;
  if (it.kind === "data") {
    return !!projGuid && String(it.data?.projectGuid || "").toUpperCase() === projGuid;
  }
  const r = (it.role || "").trim();
  return r === ent.roleLabel || r === ent.entityName;
}
function initialsOf(label: string): string {
  return (label || "")
    .replace(/\(.*?\)/g, "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// Done row — the unified shape for the "Done" view: finished history from the
// server (workflows incl. approvals granted, rhythm completions, grouped KPI
// entries) plus items handled in this session. Lifecycle events (invite +
// accept/decline/resign acks) collapse into one threaded row whose status
// mirrors the latest event. Status colors follow the locked L4E palette:
// blue = complete, grey = closed (declined/cancelled/skipped is NOT critical → not red).
type DoneStatusKind = "done" | "closed";
const DONE_STATUS_CLASS: Record<DoneStatusKind, string> = {
  done: styles.stResolved,    // blue  — complete
  closed: styles.stNeeds,     // grey  — declined/cancelled/skipped (closed, not critical)
};
interface DoneRow {
  id: string;
  typeKey: string;
  source: string;
  role: string;            // role label (rhythm tasks); "—" otherwise
  subject: string;
  addedLabel: string;      // when it entered the Action Center ("—" when none)
  dueLabel: string;        // due date ("—" when none); shown beside Completed
  dateLabel: string;       // completion date
  dateSort: number;
  status: DoneStatusKind;
  count?: number;          // grouped KPI entries
  threadKey?: string;      // lifecycle thread (assignment_guid)
  members?: DoneRow[];     // chronological lifecycle events when threaded
  retired?: boolean;       // source slot gone — role no longer held (tray note)
  workflow?: WorkflowRequest;
  // DEV-1450 §3c content retention: the raw server outcome (approved /
  // declined / completed / …) for the outcome+date line, and the project
  // guid for grouped data rows' live Open-project link.
  outcome?: string;
  projectGuid?: string;
  /** Task/duty content (rhythm rows — resolved from the client-side
   *  instance when it's in the current year window). */
  description?: string;
  /** Getting-started rows: the checklist milestone (nudge + nav CTA)
   *  so the Done tray keeps content + live navigation (§3c). */
  milestone?: Record<string, any>;
}

// Seen/resolved/"Done today" persistence moved to the shared store
// (src/state/actionItemSeenStore.ts) — §7.14 badge composer. Server seen-keys
// authoritative (DEV-1209); localStorage optimistic cache (DEV-1443
// user-namespaced, day-scoped); one set for this list AND the LeftNav badge.

const ActionCenterInbox: React.FC<Props> = ({
  currentUserGuid,
  users,
  projects,
  communities,
  relationships,
  data,
  onProjectConfigChanged,
  communityMemberships,
  projectAcl,
  allProjectHeroLookup,
  communityRoleAssignments,
}) => {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const accountSettings = useAccountSettings();
  // ── Shared item composer (§7.14, Track 4 Slice A) ──────────────────
  // The item composition (workflows, rhythms, MP tasks, data entry, checklist,
  // guidelines Notices, RACI) EXTRACTED to useActionItemComposer so the
  // app-wide LeftNav badge composes the SAME set — badge and list can never
  // diverge. Inbox mode keeps the full per-project RACI fan-out on AC mount.
  const {
    items,
    version,
    bumpVersion,
    now,
    instances,
    refetchRhythms,
    mpTasks,
    refetchMpTasks,
    engagementEntries,
    archetype,
    isExplorer,
    gsActive,
    onboardingFlags,
    onboardingFlagDates,
    invitedCommunityGuid,
    recordOnboardingMilestone,
    onboardingLoading,
    followCount,
    guidelines,
    acknowledgeGuidelines,
    tGettingStarted,
    userMap,
    communityMap,
    communityTypeMap,
    myOrgGuids,
    orgGuidelinesMap,
    projectCommunityMap,
    resolveName,
  } = useActionItemComposer({
    mode: "inbox",
    currentUserGuid,
    users,
    projects,
    communities,
    relationships,
    data,
    communityMemberships,
    projectAcl,
    allProjectHeroLookup,
    communityRoleAssignments,
  });

  // Voice-ladder (Skip 2026-07-07) — the community-standards read-and-accept
  // modal, opened from the "Read this: community standards" checklist row.
  // Accepting invalidates the security cache; the users prop refreshes and
  // the row computes complete.
  const [standardsModalOpen, setStandardsModalOpen] = useState(false);

  // "Read me first" (Skip 2026-07-07) — the welcome screen re-opened from
  // its checklist pointer; closing from this path records the ack flag.
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);

  // Org-guidelines read-to-accept (Skip 2026-07-08): the guidelines rows
  // open the CONTENT in-task (CommunityGuidelinesModal) instead of a bare
  // Acknowledge + go-look-over-there link — mirrors the standards act.
  // Holds the row being acknowledged; null = closed.
  const [guidelinesModalItem, setGuidelinesModalItem] = useState<ActionItem | null>(null);

  // In-place 2FA enrollment (architect directive 2026-07-10) — the
  // enable_2fa task launches TotpSetup right here, no settings detour.
  const [totpModalOpen, setTotpModalOpen] = useState(false);

  // §7.9 — the LeftNav Action Center highlight clears on FIRST VISIT, which is
  // exactly this surface mounting with onboarding state loaded. Server-side
  // flag (never localStorage). One write, idempotent server-side anyway.
  const acVisitedFiredRef = useRef(false);
  useEffect(() => {
    if (acVisitedFiredRef.current || onboardingLoading || !archetype) return;
    if (onboardingFlags.has("action_center_visited")) return;
    acVisitedFiredRef.current = true;
    recordOnboardingMilestone({ milestoneKey: "action_center_visited" }).catch(() => {
      acVisitedFiredRef.current = false; // allow a retry on next mount
    });
  }, [onboardingLoading, archetype, onboardingFlags, recordOnboardingMilestone]);
  const baseRoleLabel = useMemo(() => {
    const RUNG: Record<string, string> = {
      explorer: "explorer",
      contributor: "contributor",
      leader: "leader",
      governor: "governor",
    };
    // A user only reaches the Action Center after signup, at which point they
    // are at least an Explorer — so the fallback rung is "explorer", never
    // "New member" (Skip 2026-06-23).
    const rung = t(`actionCenter.inbox.rung.${(archetype && RUNG[archetype]) || "explorer"}`);
    // "Life4Earth {Trust Rung}" — the role IS the brand + the user's trust rung.
    // e.g. "Life4Earth Explorer".
    return `${t("actionCenter.inbox.rail.base")} ${rung}`;
  }, [archetype, t]);

  const [view, setView] = useState<View>("todo");
  const [query, setQuery] = useState("");
  const [readFilter, setReadFilter] = useState<ReadFilter>("all");
  // Shared seen/resolved store (§7.14 / DEV-1209): server seen-state is
  // authoritative, localStorage is the optimistic cache, and the LeftNav
  // badge reads the SAME sets — count and list never diverge.
  const {
    seenIds,
    resolvedIds,
    doneSnapshots,
    markSeen,
    markResolved: storeMarkResolved,
    unresolve,
  } = useActionItemSeenStore(currentUserGuid);
  const [completed, setCompleted] = useState<CompletedActionItem[]>([]);
  // Default All Actions to a flat, newest-first sort (Skip 2026-07-07):
  // "added" descending = most recently arrived at top (inbox convention).
  // Column headers re-sort from here.
  const [sortKey, setSortKey] = useState<SortKey>("added");
  const [sortDir, setSortDir] = useState<1 | -1>(-1);
  const [selId, setSelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [eulaOpen, setEulaOpen] = useState(false);
  // SPEC_ROLE_ORGANIZED_INBOX §4a — entity-grouped rail (To do view).
  // Default All Actions to the FLAT sortable list (Skip 2026-07-07) so the
  // column headers sort the WHOLE list — the grouped-by-community view only
  // reorders within groups, which read as "columns don't sort." The "role"
  // grouping stays available via the segmented toggle. (Directionally the
  // §7.1 community-as-filter move; small/reversible default flip — flagged to
  // the architect, not the full §7 restructuring.)
  const [groupMode, setGroupMode] = useState<GroupMode>("flat");
  // Rail selection: "all" · "inv" · ROLE_BASE_KEY · a community/other group key
  // · a composite "community:GUID::role:Label" · "eng:<engagementKey>" (a role).
  const [railSel, setRailSel] = useState<string>("all");
  // Slice C″ — reverse bridge: /actions?role=<engagement key> lands with that
  // role's card selected (mirror of the role page's "Tasks in Action Center").
  // Read once on mount; the eng: selection resolves as entries hydrate.
  const [searchParams] = useSearchParams();
  const roleParamRef = useRef(false);
  useEffect(() => {
    // The ref guard makes this once-only regardless of param changes.
    if (roleParamRef.current) return;
    roleParamRef.current = true;
    const rk = searchParams.get("role");
    if (rk) setRailSel(`eng:${rk}`);
  }, [searchParams]);
  const [railQuery, setRailQuery] = useState("");
  // "Show previous roles" — reveals ended/closed roles (completed assignments,
  // terminal-project roles) in the rail, rendered muted (DEV-1368).
  const [showPrevious, setShowPrevious] = useState(false);
  // Active tab in the selected-role detail. Track 4 Slice D (§7.1 / DEV-1425):
  // the "About this Role" tab + rhythm panel + Resign RELOCATED to the
  // Memberships tray — the Action Center is obligations-only. The role detail
  // keeps Assigned tasks (+ the RACI Digest for full-project roles) and a
  // compact context header linking to the Memberships reference view.
  const [roleTab, setRoleTab] = useState<"tasks" | "digest">("tasks");
  // Collapsible Upcoming / Completed sections in a role's Assigned tasks
  // (Current = due+overdue, always open; the others collapsed). Skip 2026-06-22.
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  // Collapsed community groups in the rail (default expanded).
  // Option C rail (DEV-1368): communities are COLLAPSED by default; we persist
  // which the user has expanded so the choice survives reloads (localStorage
  // interim; server-side in Slice G). Stores the EXPANDED set — empty = all
  // collapsed, the spec's community-first empty state.
  const RAIL_EXPANDED_KEY = "l4e_ac_rail_expanded";
  const [expandedRail, setExpandedRail] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(RAIL_EXPANDED_KEY);
      return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });
  const toggleRailGroup = useCallback((key: string) => {
    setExpandedRail((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      try {
        localStorage.setItem(RAIL_EXPANDED_KEY, JSON.stringify(Array.from(n)));
      } catch {
        /* persistence is best-effort */
      }
      return n;
    });
  }, []);

  // Size the surface to fill the viewport down to the window bottom. Measured
  // (not a viewport calc) so it's correct regardless of the app chrome above.
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [surfaceH, setSurfaceH] = useState<number | undefined>(undefined);
  useEffect(() => {
    const measure = () => {
      const el = surfaceRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setSurfaceH(Math.max(320, Math.round(window.innerHeight - top - 16)));
    };
    measure();
    window.addEventListener("resize", measure);
    const settle = window.setTimeout(measure, 200); // after fonts/chips reflow
    return () => {
      window.removeEventListener("resize", measure);
      window.clearTimeout(settle);
    };
  }, []);

  // Undo toast for the optimistic resolved layer (state itself lives in the
  // shared seen/resolved store above).
  const [undo, setUndo] = useState<{ id: string; label: string } | null>(null);

  // ── Done rows ──
  // Finished history from /api/action-items/completed plus items handled this
  // session. Lifecycle events (invite + accept/decline/resign acks) that share
  // an assignment_guid collapse into ONE threaded row whose status mirrors the
  // latest event; the tray lists the full chronology. KPI entries group per
  // project+section.
  const doneRows = useMemo<DoneRow[]>(() => {
    void version;
    const fmt = (iso: string) => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    };
    const DONE = new Set(["approved", "completed", "entered", "acknowledged"]);
    const rows: DoneRow[] = [];
    const dataGroups = new Map<
      string,
      { proj: string; section: string; latest: number; count: number }
    >();
    const threads = new Map<string, DoneRow[]>();
    // DEV-1450 §3c — resolve the FULL workflow for workflow-backed history
    // rows (itemKey = workflow_guid) so the Done tray keeps description,
    // payload links, and the verdict actor. Content is never discarded.
    const wfByGuid = new Map(
      getAllWorkflows().map((w) => [String(w.workflow_guid).toUpperCase(), w]),
    );

    for (const c of completed) {
      const ts = (() => {
        const v = new Date(c.completedAt).getTime();
        return Number.isNaN(v) ? 0 : v;
      })();
      if (c.kind === "data") {
        const key = `${c.projectId || "?"}:${c.dataSection || "?"}`;
        const g = dataGroups.get(key);
        if (g) {
          g.count += 1;
          if (ts > g.latest) g.latest = ts;
        } else {
          dataGroups.set(key, {
            proj: String(c.projectId || ""),
            section: String(c.dataSection || ""),
            latest: ts,
            count: 1,
          });
        }
        continue;
      }
      const status: DoneStatusKind = DONE.has(c.outcome) ? "done" : "closed";
      const typeKey =
        c.kind === "workflow" ? typeKeyFor(eventKind(c.actionType || "")) : typeKeyFor("rhythm");
      // Source: backend-resolved label (rhythm → community) when present, else
      // resolve the counterparty guid (workflow other-party). Role: rhythm only.
      const source = c.sourceLabel || resolveName(c.counterpartyGuid) || "—";
      const row: DoneRow = {
        id: c.itemKey,
        typeKey,
        source,
        role: c.roleLabel || "—",
        subject: c.subject || "—",
        addedLabel: c.addedAt ? fmt(c.addedAt) : "—",
        dueLabel: c.dueAt ? fmt(c.dueAt) : "—",
        dateLabel: fmt(c.completedAt),
        dateSort: ts,
        status,
        retired: c.sourceRetired || undefined,
        threadKey: c.threadKey || undefined,
        outcome: c.outcome || undefined,
        workflow:
          c.kind === "workflow"
            ? wfByGuid.get(String(c.itemKey).toUpperCase())
            : undefined,
        // Rhythm rows: itemKey = instanceGuid — the completed instance is
        // usually still in the client's year window, so its description
        // (the duty content) stays readable in the Done tray (§3c).
        description:
          c.kind === "rhythm"
            ? (instances || []).find(
                (i) =>
                  String(i.instanceGuid).toUpperCase() ===
                  String(c.itemKey).toUpperCase(),
              )?.description || undefined
            : undefined,
      };
      if (c.kind === "workflow" && c.threadKey) {
        const arr = threads.get(c.threadKey);
        if (arr) arr.push(row);
        else threads.set(c.threadKey, [row]);
      } else {
        rows.push(row);
      }
    }

    // Collapse each lifecycle thread to one row = the latest event; members
    // carry the full chronology for the tray. Status mirrors the latest event.
    for (const [key, members] of threads) {
      const sorted = [...members].sort((a, b) => a.dateSort - b.dateSort);
      const latest = sorted[sorted.length - 1];
      rows.push({
        ...latest,
        id: `thread:${key}`,
        threadKey: key,
        members: sorted,
        count: sorted.length > 1 ? sorted.length : undefined,
      });
    }

    for (const [key, g] of dataGroups) {
      rows.push({
        id: `done-data:${key}`,
        typeKey: typeKeyFor("data"),
        source: resolveName(g.proj) || "—",
        role: t("actionCenter.inbox.role.team"),
        subject: t(`actionCenter.inbox.dataSection.${g.section}`),
        addedLabel: "—",
        dueLabel: "—",
        dateLabel: g.latest ? fmt(new Date(g.latest).toISOString()) : "—",
        dateSort: g.latest,
        status: "done",
        count: g.count,
        outcome: "entered",
        projectGuid: g.proj || undefined,
      });
    }

    // ── Getting-started history (Skip 2026-07-07) ──
    // Completed checklist rows retire from To-do at promotion, but they are
    // real history. No server rows exist (completion computes from live
    // data), so synthesize Done entries from the same builder + profile
    // flags — durable across reloads and devices, unlike the day-scoped
    // session snapshots. Gated on the promotion flag so long-standing
    // accounts don't grow retroactive rows.
    if (!isExplorer && onboardingFlags.has("contributor_promoted") && currentUserGuid) {
      const meUpper = String(currentUserGuid).toUpperCase();
      const cu = (users || []).find(
        (u: any) => String(u?.user_guid || "").toUpperCase() === meUpper,
      );
      try {
        const achieved = evaluateMilestones({ currentUserGuid, currentUser: cu } as any);
        const orgGuidUpper = invitedCommunityGuid
          ? String(invitedCommunityGuid).toUpperCase()
          : null;
        const gsRows = gettingStartedToActionItems(
          {
            profileComplete: achieved.has("complete_profile"),
            followCount,
            achievedFlags: onboardingFlags as ReadonlySet<string>,
            orgCommunityGuid: invitedCommunityGuid || null,
            orgCommunityName: orgGuidUpper
              ? communityMap.get(orgGuidUpper) || null
              : null,
            guidelines: guidelines
              ? { hasContent: !!guidelines.content, acknowledged: guidelines.acknowledged }
              : null,
            signupAt: cu?.created_at ?? null,
            standardsCurrent:
              !!cu?.community_standards_accepted_at &&
              (cu?.community_standards_version_accepted ?? 0) >= STANDARDS_CURRENT_VERSION,
          },
          { t: tGettingStarted },
        );
        // Getting-started work was done AS an Explorer — that's the row's role
        // (Skip 2026-07-07: role must not render "—" on this history).
        const explorerRole = t("actionCenter.inbox.rung.explorer");
        // Best-known completion timestamps per row. Rows whose completion is
        // a computed state with no recorded moment (profile, follows) fall
        // back to the PROMOTION timestamp — every rung basic was necessarily
        // complete by the instant the promotion fired, so it is an honest
        // upper bound (Skip 2026-07-07: Completed must not render "—").
        const promotedIso = onboardingFlagDates["contributor_promoted"] || null;
        const dateOf = (key?: string): string | null => {
          if (key === "platform_standards") {
            return cu?.community_standards_accepted_at || promotedIso;
          }
          if (key === "meet_org") {
            return onboardingFlagDates["org_orientation_done"] || promotedIso;
          }
          if (key === "org_guidelines") {
            return guidelines?.myAcknowledgedAt || promotedIso;
          }
          return promotedIso;
        };
        for (const it of gsRows) {
          if (it.status !== "resolved") continue;
          const iso = dateOf(it.checklistKey);
          rows.push({
            id: it.id,
            typeKey: it.typeKey,
            source: it.source,
            role: explorerRole,
            subject: it.subject,
            addedLabel: it.addedLabel || "—",
            dueLabel: "—",
            dateLabel: iso ? fmt(iso) : "—",
            dateSort: iso ? new Date(iso).getTime() : it.addedSort || 0,
            status: "done",
            // DEV-1450 §3c (Skip repro 2026-07-10): checklist rows in the
            // DONE view were collapsing to "This item is complete." Carry
            // the content (nudge) + milestone (nav CTA) so the Done tray
            // renders the same substance as the To-do tray did.
            outcome: "completed",
            description: (it.milestone as any)?.nudgeText || undefined,
            milestone: it.milestone as Record<string, any> | undefined,
          });
        }
      } catch {
        /* live inputs may be empty on first render; history reappears next render */
      }
    }

    // The acknowledged promotion Notice — its Done tray keeps the
    // explanation and can re-open the Contributor welcome. Deliberately
    // OUTSIDE the checklist-synthesis try/gate above (Skip bug report
    // 2026-07-08: an exception in the milestone evaluator was silently
    // swallowing this row along with the checklist history). The ack flag
    // only ever exists post-promotion, so it is the one condition needed.
    if (onboardingFlags.has("contributor_promotion_ack")) {
      const promoIso = onboardingFlagDates["contributor_promoted"] || null;
      const ackIso = onboardingFlagDates["contributor_promotion_ack"] || promoIso;
      rows.push({
        id: "gs:promotion_notice",
        typeKey: typeKeyFor("notice"),
        source: "Life4Earth",
        role: t("actionCenter.inbox.rung.explorer"),
        subject: t("actionCenter.inbox.gettingStarted.promotion.subject"),
        addedLabel: promoIso ? fmt(promoIso) : "—",
        dueLabel: "—",
        dateLabel: ackIso ? fmt(ackIso) : "—",
        dateSort: ackIso ? new Date(ackIso).getTime() : 0,
        status: "done",
        outcome: "acknowledged",
      });
    }

    // Items handled this session (optimistic) not yet in server history.
    const serverIds = new Set(rows.map((r) => r.id));
    for (const snap of doneSnapshots.values()) {
      if (serverIds.has(snap.id)) continue;
      // DEV-1450 §3c (Skip repro 2026-07-10): session snapshots are trimmed
      // display rows — re-attach content at render time. Workflow items'
      // ids ARE the workflow_guid, so the full request (description, links,
      // verdict history) resolves from the store; the workflow status is
      // the outcome.
      const snapWf = wfByGuid.get(String(snap.id).toUpperCase());
      rows.push({
        id: snap.id,
        typeKey: snap.typeKey,
        source: snap.source,
        role: snap.role || "—",
        subject: snap.subject,
        addedLabel: snap.addedLabel || "—",
        dueLabel: snap.dateLabel || "—",
        dateLabel: t("actionCenter.inbox.justNow"),
        dateSort: Date.now(),
        status: "done",
        workflow: snapWf,
        outcome: snapWf ? String(snapWf.status || "") || undefined : undefined,
        description: (snap as any).description || undefined,
        milestone: (snap.milestone as Record<string, any>) || undefined,
      });
    }
    return rows;
  }, [
    version,
    completed,
    instances,
    doneSnapshots,
    resolveName,
    t,
    // Getting-started Done synthesis (async-hook data — CLAUDE.md deps rule).
    isExplorer,
    onboardingFlags,
    onboardingFlagDates,
    currentUserGuid,
    users,
    invitedCommunityGuid,
    followCount,
    guidelines,
    communityMap,
    tGettingStarted,
  ]);

  // Clear the inbox search whenever the rail selection changes. Hiding the input
  // in the role view is not enough on its own — the query is component state, so
  // a search typed in All Actions would keep filtering (invisibly) after clicking
  // into a role, hiding that role's tasks with no visible cause. Skip 2026-07-10.
  useEffect(() => {
    setQuery("");
  }, [railSel]);

  // ── Search + filter + sort ──
  const matchesQuery = useCallback(
    (text: string) => !query || text.toLowerCase().includes(query.toLowerCase()),
    [query],
  );

  // To do = open items in my court (not yet resolved). Read axis + search + sort.
  const visibleItems = useMemo(() => {
    // status "resolved" = already-done (e.g. a completed Gantt RACI task). The
    // active list is for actionable work; done items surface in Completed/Done.
    // EXCEPTION — Getting-started checklist rows (§7.10 r7): completed rungs
    // stay visible with the handled check so the checklist reads as a
    // checklist ("2 of 4 done"), until promotion retires the whole set.
    let list = items.filter(
      (it) => it.gettingStarted || (!resolvedIds.has(it.id) && it.status !== "resolved"),
    );
    if (readFilter === "unseen") {
      list = list.filter((it) => !(seenIds.has(it.id) || resolvedIds.has(it.id)));
    } else if (readFilter === "seen") {
      list = list.filter((it) => seenIds.has(it.id) || resolvedIds.has(it.id));
    }
    list = list.filter((it) => matchesQuery(`${it.subject} ${it.source} ${t(it.typeKey)}`));
    const dir = sortDir;
    list = [...list].sort((a, b) => {
      let r = 0;
      switch (sortKey) {
        case "priority":
          r = actionItemScore(b) - actionItemScore(a);
          return r; // priority is inherently descending; ignore dir
        case "type":
          r = t(a.typeKey).localeCompare(t(b.typeKey));
          break;
        case "source":
          r = a.source.localeCompare(b.source);
          break;
        case "role":
          r = (a.role || "").localeCompare(b.role || "");
          break;
        case "added":
          r = (a.addedSort ?? Number.MAX_SAFE_INTEGER) - (b.addedSort ?? Number.MAX_SAFE_INTEGER);
          break;
        case "date":
          r = a.dateSort - b.dateSort;
          break;
        case "status":
          r = a.status.localeCompare(b.status);
          break;
      }
      return r * dir;
    });
    return list;
  }, [items, resolvedIds, seenIds, readFilter, matchesQuery, sortKey, sortDir, t]);

  const visibleDone = useMemo(() => {
    const list = doneRows.filter((s) => matchesQuery(`${s.subject} ${s.source} ${t(s.typeKey)}`));
    return [...list].sort((a, b) => b.dateSort - a.dateSort);
  }, [doneRows, matchesQuery, t]);

  // Done list scoped to the selected rail entry. All Actions = everything; the
  // base role (Life4Earth) = only its system completions (2FA, EULA, profile);
  // Invitations = invitation lifecycle. Skip 2026-06-22.
  const railFilteredDone = useMemo(() => {
    if (railSel === ROLE_BASE_KEY)
      // Base "Life4Earth" role = its system completions (2FA, EULA, profile,
      // getting-started rungs) PLUS the one-time Contributor promotion
      // celebration. The latter is a base-role Life4Earth item but typed
      // `notice` (it overrides GS_BASE's system type), so a system-only filter
      // dropped it from the base-role Done view (Skip bug report 2026-07-08).
      // Matched by its stable id rather than `typeKey === notice` so unrelated
      // governance/workflow notices (other roles) don't leak into the base role.
      // TODO(architect): a cleaner long-term fix tags DoneRows with
      // roleGroupKey and filters the base role by that instead of by type.
      return visibleDone.filter(
        (dr) => dr.typeKey === typeKeyFor("system") || dr.id === "gs:promotion_notice",
      );
    if (railSel === "inv")
      return visibleDone.filter((dr) => dr.typeKey === typeKeyFor("invitation"));
    return visibleDone;
  }, [visibleDone, railSel]);


  // ── Entity-grouped rail model (SPEC_ROLE_ORGANIZED_INBOX §4a) ──
  type RailRole = { roleLabel: string; key: string; items: ActionItem[]; pillar: RolePillar };
  type RailGroup = {
    key: string;
    label: string;
    items: ActionItem[];
    topScore: number;
    pillar: RolePillar;
    /** Roles held within this community (rhythm items + project tasks). */
    roles: RailRole[];
  };
  // Resolve a raw group (from groupActionItems) into a display group: label +
  // container pillar from the owning community's entity_type, plus the roles
  // nested inside it (§4a). role:base → the base role; other → the catch-all.
  const decorateGroup = useCallback(
    (g: { key: string; label: string; items: ActionItem[]; topScore: number }): RailGroup => {
      // Container pillar + label first, so nested roles can fall back to it.
      let pillar: RolePillar = "other";
      let label = g.label;
      if (g.key === ROLE_BASE_KEY) {
        pillar = "base";
        label = t("actionCenter.inbox.rail.base");
      } else if (g.key === ROLE_OTHER_KEY) {
        pillar = "other";
        label = t("actionCenter.inbox.rail.other");
      } else if (g.key.startsWith("community:") && !g.key.startsWith("community:name:")) {
        const guid = g.key.slice("community:".length).toUpperCase();
        pillar = pillarForEntityType(communityTypeMap.get(guid));
        label = communityMap.get(guid) || g.label;
      }
      // Nest items by their role label. A role's tile pillar is the item's own
      // pillar (e.g. a project task = project/grey) under the org's container
      // pillar; rhythm roles inherit the container pillar.
      const byRole = new Map<string, ActionItem[]>();
      for (const it of g.items) {
        const r = (it.role || "").trim();
        if (!r) continue;
        const arr = byRole.get(r);
        if (arr) arr.push(it);
        else byRole.set(r, [it]);
      }
      const roles: RailRole[] = Array.from(byRole.entries())
        .map(([roleLabel, items]) => ({
          roleLabel,
          key: `${g.key}::role:${roleLabel}`,
          items,
          pillar: items[0]?.pillar ?? pillar,
        }))
        .sort((a, b) => actionItemScore(b.items[0]) - actionItemScore(a.items[0]));

      return { ...g, pillar, label, roles };
    },
    [t, communityTypeMap, communityMap],
  );

  // Rail structure over the active (unresolved) set: pinned Invitations, the
  // base role, and community groups (incl. the "other" catch-all).
  const railModel = useMemo(() => {
    const active = items.filter((it) => !resolvedIds.has(it.id));
    const invitations = active.filter((it) => it.kind === "invitation");
    const base = active.filter((it) => it.roleGroupKey === ROLE_BASE_KEY);
    const rest = active.filter(
      (it) => it.kind !== "invitation" && it.roleGroupKey !== ROLE_BASE_KEY,
    );
    const groups = groupActionItems(rest).map(decorateGroup);
    return { invitations, base, groups };
  }, [items, resolvedIds, decorateGroup]);

  // §7.12 / DEV-1436 first-run disclosure — the rail's visibility gate now lives
  // BELOW engagementCommunities (the rail's own content), because that is what it
  // must count. See the block after that memo.

  const matchesRail = useCallback(
    (label: string) => !railQuery || label.toLowerCase().includes(railQuery.toLowerCase()),
    [railQuery],
  );

  // Grouped detail sections for the "All Actions" view (over the filtered/sorted
  // visibleItems): base first, then community groups by priority, then "other",
  // then Invitations. Grouping nests priority; it never reorders within a group.
  const detailSections = useMemo<RailGroup[]>(() => {
    const inv = visibleItems.filter((it) => it.kind === "invitation");
    const base = visibleItems.filter((it) => it.roleGroupKey === ROLE_BASE_KEY);
    const rest = visibleItems.filter(
      (it) => it.kind !== "invitation" && it.roleGroupKey !== ROLE_BASE_KEY,
    );
    const groups = groupActionItems(rest).map(decorateGroup);
    const out: RailGroup[] = [];
    if (base.length)
      out.push({ key: ROLE_BASE_KEY, label: t("actionCenter.inbox.rail.base"), items: base, topScore: 0, pillar: "base", roles: [] });
    out.push(...groups);
    if (inv.length)
      out.push({ key: "inv", label: t("actionCenter.inbox.rail.invitations"), items: inv, topScore: 0, pillar: "other", roles: [] });
    return out;
  }, [visibleItems, decorateGroup, t]);

  // Detail rows when a specific rail entry is selected (or grouping is off).
  const railFilteredItems = useMemo(() => {
    if (railSel === "all") return visibleItems;
    // Selected role card ("eng:<entryKey>") — match this engagement's action
    // items by owning community + role label / project name (§4a join).
    if (railSel.startsWith("eng:")) {
      const k = railSel.slice(4);
      const ent = engagementEntries.find((e) => e.key === k);
      if (!ent) return [];
      return visibleItems.filter((it) => itemMatchesEntry(it, ent));
    }
    // Composite "community:GUID::role:Label" — a single role within a community.
    const sep = railSel.indexOf("::role:");
    if (sep >= 0) {
      const gk = railSel.slice(0, sep);
      const roleLabel = railSel.slice(sep + "::role:".length);
      return visibleItems.filter(
        (it) => it.roleGroupKey === gk && (it.role || "").trim() === roleLabel,
      );
    }
    if (railSel === "inv") return visibleItems.filter((it) => it.kind === "invitation");
    if (railSel === ROLE_BASE_KEY)
      return visibleItems.filter((it) => it.roleGroupKey === ROLE_BASE_KEY);
    if (railSel === ROLE_OTHER_KEY)
      return visibleItems.filter(
        (it) => it.kind !== "invitation" && (it.roleGroupKey || ROLE_OTHER_KEY) === ROLE_OTHER_KEY,
      );
    return visibleItems.filter((it) => it.roleGroupKey === railSel);
  }, [visibleItems, railSel, engagementEntries]);

  // The current user's avatar + name — used for the "My Workshop" group avatar.
  const currentUserAvatar = useMemo(() => {
    const me = String(currentUserGuid || "").toUpperCase();
    const u = (users || []).find(
      (x: any) => String(x?.user_guid || x?.userGuid || x?.guid || "").toUpperCase() === me,
    );
    const raw = (u as any)?.profile_image_url || (u as any)?.profileImageUrl || null;
    return {
      url: raw ? mediaSrc(raw) : null,
      name:
        (u as any)?.display_name || (u as any)?.displayName || (u as any)?.name || "",
    };
  }, [users, currentUserGuid]);

  // Resolved avatar URL by user GUID — the reliable source for workshop-owner
  // faces (the workspace DTO's leadUser.avatarUrl is often null → initials).
  const userAvatarById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users || []) {
      const g = String((u as any).user_guid || (u as any).userGuid || (u as any).guid || "").toUpperCase();
      const raw = (u as any).profile_image_url || (u as any).profileImageUrl;
      if (g && raw) m.set(g, mediaSrc(raw));
    }
    return m;
  }, [users]);

  // Geo flag resolver — walk the community hierarchy to the geo_country
  // ancestor and return its flag emoji (the inherited-flag avatar geo
  // community headers use). Org/non-geo communities return "" → fall back to
  // their logo. Mirrors findCountryFlag on the community pages (DEV-1368).
  const flagFor = useCallback(
    (guid: string | null | undefined): string => {
      if (!guid) return "";
      const byGuid = new Map<string, any>();
      for (const c of communities || []) {
        const g = String(c.entity_guid || c.entityGuid || c.guid || "").toUpperCase();
        if (g) byGuid.set(g, c);
      }
      const parent = new Map<string, string>();
      for (const r of relationships || []) {
        const child = String((r as any).child_entity_guid || (r as any).childEntityGuid || "").toUpperCase();
        const par = String((r as any).parent_entity_guid || (r as any).parentEntityGuid || "").toUpperCase();
        if (child && par) parent.set(child, par);
      }
      const codeOf = (e: any) => e?.code || e?.country_code || null;
      const typeOf = (e: any) => String(e?.entity_type || e?.entityType || "").toLowerCase();
      let cur: string | undefined = guid.toUpperCase();
      for (let i = 0; i < 6 && cur; i++) {
        const e = byGuid.get(cur);
        if (e && typeOf(e) === "geo_country") return countryFlag(codeOf(e));
        cur = parent.get(cur);
      }
      return "";
    },
    [communities, relationships],
  );

  // Community-grouped role cards for the rail (§4a). Each group = an org/geo
  // community the user holds roles in; entries are the EngagementCard roles.
  const engagementCommunities = useMemo(() => {
    // Within a community, order roles: the home (community/org) role first,
    // then project, problem, solution (Skip, 2026-06-22).
    const ORDER: Record<string, number> = {
      communities: 0,
      enablers: 0,
      projects: 1,
      problems: 2,
      solutions: 3,
    };
    type Anno = { e: SelectableEntry; count: number; overdue: boolean };
    type Grp = {
      guid: string;
      norm: string;
      name: string;
      entries: Anno[];
      logoUrl: string | null;
      ring: RolePillar;
      subtitle: string;
      family: string;
      ownerUserGuid: string | null;
      flagEmoji: string;
      total: number;
      overdue: boolean;
    };
    const map = new Map<string, Grp>();
    const meUpper = String(currentUserGuid || "").toUpperCase();
    // Previous (ended/closed) roles are hidden unless the user toggles them on.
    const sourceEntries = showPrevious
      ? engagementEntries
      : engagementEntries.filter((e) => !e.isPrevious);
    for (const e of sourceEntries) {
      // Problem workspaces are personal — they belong to a WORKSHOP keyed by the
      // owning user (teamLeadGuid), NOT to a community (Skip 2026-06-23). Your
      // own workspaces consolidate under "My Workshop"; others' under
      // "{name}'s Workshop". The avatar is that user's profile.
      const isWorkspace = e.typeTag === "workspace";
      // A project with NO owning community is a personal initiative the user
      // created → it belongs in their "Personal Roles" home, not "Other"
      // (Skip 2026-06-23).
      const isCommunitylessProject =
        (e.typeTag === "full-project" || e.typeTag === "mini-project") && !e.groupCommunityGuid;
      let norm: string;
      let name: string;
      let wsAvatar: string | null = null;
      let ownerGuid: string | null = null;
      if (isWorkspace) {
        const ws = e.source.data as any;
        const owner = String(ws.teamLeadGuid || "").toUpperCase();
        const isSelf = !owner || owner === meUpper;
        norm = isSelf ? "workshop" : `workshop:${owner}`;
        name = isSelf
          ? t("actionCenter.inbox.rail.personalRoles")
          : t("actionCenter.inbox.rail.othersWorkshop", {
              name: ws.leadUser?.name || ws.communityName || "",
            });
        // Owner's face from the security-users list (reliable); leadUser /
        // current-user avatar as fallbacks (Skip 2026-06-24).
        wsAvatar = isSelf
          ? userAvatarById.get(meUpper) || currentUserAvatar.url
          : (owner ? userAvatarById.get(owner) : null) || ws.leadUser?.avatarUrl || null;
        ownerGuid = isSelf ? currentUserGuid || null : owner;
      } else if (isCommunitylessProject) {
        norm = "workshop";
        name = t("actionCenter.inbox.rail.personalRoles");
        wsAvatar = userAvatarById.get(meUpper) || currentUserAvatar.url;
        ownerGuid = currentUserGuid || null;
      } else {
        // Normalize the GUID so the SAME community in different casings merges
        // into ONE group (Boston-listed-twice bug, Skip).
        norm = (e.groupCommunityGuid || "").toUpperCase() || "__none__";
        name = e.groupCommunityName || e.entityName || t("actionCenter.inbox.rail.other");
      }
      const isHomeKey = norm.startsWith("workshop");
      let g = map.get(norm);
      if (!g) {
        g = {
          guid: isHomeKey ? norm : e.groupCommunityGuid || norm,
          norm,
          name,
          entries: [],
          logoUrl: isHomeKey ? wsAvatar : null,
          ring: isHomeKey ? "base" : "community",
          subtitle: "",
          family: "",
          ownerUserGuid: ownerGuid,
          flagEmoji: "",
          total: 0,
          overdue: false,
        };
        map.set(norm, g);
      }
      // Former roles carry no live responsibility — their tasks aren't the
      // user's anymore, so they contribute NO count to the row or the group
      // header (Skip 2026-06-24).
      const prev = !!e.isPrevious;
      const matched = prev ? [] : visibleItems.filter((it) => itemMatchesEntry(it, e));
      const overdue = matched.some((it) => it.status === "overdue");
      g.entries.push({ e, count: matched.length, overdue });
      g.total += matched.length;
      if (overdue) g.overdue = true;
      // The community/org entry is the avatar subject + ring pillar source.
      if (e.typeTag === "community" || e.typeTag === "organization") {
        if (e.logoUrl) g.logoUrl = e.logoUrl;
        g.ring = e.typeTag === "organization" ? "enabler" : "community";
      }
    }
    // "Personal Roles" is the user's home — always present (it always holds at
    // least the Life4Earth role, folded in at render), so it never vanishes
    // (Skip 2026-06-23). Its avatar is the user's own face.
    if (!map.has("workshop")) {
      map.set("workshop", {
        guid: "workshop",
        norm: "workshop",
        name: t("actionCenter.inbox.rail.personalRoles"),
        entries: [],
        logoUrl: currentUserAvatar.url,
        ring: "base",
        subtitle: "",
        family: "",
        ownerUserGuid: currentUserGuid || null,
        flagEmoji: "",
        total: 0,
        overdue: false,
      });
    }
    for (const g of map.values()) {
      g.entries.sort((a, b) => (ORDER[a.e.pillar] ?? 9) - (ORDER[b.e.pillar] ?? 9));
      const isWs = g.guid.startsWith("workshop");
      // Geo communities show their inherited country flag; org/workshop don't.
      g.flagEmoji = isWs ? "" : flagFor(g.guid);
      const path = !isWs && g.guid && g.guid !== "__none__" ? resolveCommunityPath(g.guid) : [];
      // Family root for clustering — the top ancestor (e.g. a Rotary club's
      // root is "Rotary International"; a city's is its country). Same-family
      // communities sort adjacent (Skip 2026-06-23).
      g.family = path.length ? path[0].name : g.name;
      // Subtitle is role-forward for communities/orgs (Skip 2026-06-23): one
      // role → its name; multiple → "Lead +N more roles". Workshops keep their
      // personal/owner subtitle. (Geo/parent context now lives on the role
      // detail header's linked breadcrumb, and the avatar signals geo-vs-org.)
      if (g.guid === "workshop") {
        // Personal Roles: Life4Earth is folded in as the first role, so the
        // role count is 1 (Life4Earth) + the entries (investigations +
        // community-less projects).
        const n = g.entries.length;
        g.subtitle =
          n === 0
            ? baseRoleLabel
            : `${baseRoleLabel} ${t("actionCenter.inbox.rail.plusMoreRoles", { count: n })}`;
      } else if (isWs) {
        // Other-user workshops: role-forward like communities.
        const n = g.entries.length;
        g.subtitle =
          n === 1
            ? g.entries[0].e.roleLabel || ""
            : n > 1
              ? `${g.entries[0].e.roleLabel} ${t("actionCenter.inbox.rail.plusMoreRoles", { count: n - 1 })}`
              : "";
      } else {
        const n = g.entries.length;
        g.subtitle =
          n === 1
            ? g.entries[0].e.roleLabel || ""
            : n > 1
              ? `${g.entries[0].e.roleLabel} ${t("actionCenter.inbox.rail.plusMoreRoles", { count: n - 1 })}`
              : "";
      }
    }
    // My Workshop pinned first; then cluster communities by family root and
    // sort alphabetically within a family (adjacency clustering, Skip).
    return Array.from(map.values()).sort((a, b) => {
      if (a.guid === "workshop") return -1;
      if (b.guid === "workshop") return 1;
      const fam = a.family.toLowerCase().localeCompare(b.family.toLowerCase());
      if (fam !== 0) return fam;
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  }, [engagementEntries, visibleItems, currentUserGuid, currentUserAvatar, userAvatarById, flagFor, baseRoleLabel, showPrevious, t]);

  // §7.12 / DEV-1436 — first-run disclosure. The rail appears only when there are
  // ≥2 things to navigate BETWEEN. A day-zero Explorer, whose only role is the
  // Life4Earth {rung} identity, sees a rail-less single-column view — simplified,
  // never emptied. Mirrors the member-lens Communities-rail rule (§8.10, "a
  // one-item rail is worse than no rail").
  //
  // FIX 2026-07-10 (Skip hit this live). This previously counted railModel.groups
  // — ACTION ITEMS grouped by community. That was wrong twice over:
  //
  //   1. WRONG SOURCE. The rail renders ROLES (engagementCommunities), not items.
  //      The two diverge, so a user holding three roles could lose the entire
  //      navigator because their items happened to cluster into <2 communities.
  //      That is exactly what happened after resigning a single role: Boston,
  //      Hampton Rotary Club and Life4Earth Global were all still active, and the
  //      rail vanished anyway.
  //   2. RACE. `items` streams in asynchronously (workflows, RACI, rhythms and
  //      mini-project tasks each resolve separately), so the count dipped below 2
  //      mid-load and the rail DELETED and re-inserted itself.
  //
  // Counting the rail's own rendered groups fixes both: it is the same source as
  // the content (they cannot diverge), and it settles once instead of streaming.
  // engagementCommunities always includes the pinned "Personal Roles" home, so a
  // day-zero Explorer still counts exactly 1 → no rail, preserving the intent.
  const navigableGroupCount =
    engagementCommunities.length + (railModel.invitations.length > 0 ? 1 : 0);
  const railVisible = navigableGroupCount >= 2;

  // If the rail hides while a filter is active, fall back to All Actions so the
  // user is never stranded filtered into a rail they can no longer see.
  useEffect(() => {
    if (!railVisible && railSel !== "all") setRailSel("all");
  }, [railVisible, railSel]);

  // Graceful unlock reveal (§7.12 caveat 2): pulse ONLY when the user has
  // actually experienced the rail-less state with content this session —
  // never on an ordinary load where data resolves straight to ≥2 groupings.
  const seenHiddenWithDataRef = useRef(false);
  const [railRevealPulse, setRailRevealPulse] = useState(false);
  useEffect(() => {
    if (!railVisible) {
      if (items.length > 0) seenHiddenWithDataRef.current = true;
      return;
    }
    if (seenHiddenWithDataRef.current) {
      seenHiddenWithDataRef.current = false;
      setRailRevealPulse(true);
      const tm = window.setTimeout(() => setRailRevealPulse(false), 4200);
      return () => window.clearTimeout(tm);
    }
  }, [railVisible, items.length]);

  // The role card currently selected in the rail (drives the three-tab detail).
  const selectedEngagement = useMemo<SelectableEntry | null>(
    () =>
      railSel.startsWith("eng:")
        ? engagementEntries.find((e) => e.key === railSel.slice(4)) || null
        : null,
    [railSel, engagementEntries],
  );

  // Resign + "About this Role" RELOCATED to the Memberships tray (Track 4
  // Slices C/D — §7.1: the Action Center is obligations-only; role reference
  // and belonging changes live on Memberships).

  // Assigned-tasks sections for the selected role: Current (due+overdue),
  // Upcoming (not-yet-due + pending cadence instances), Completed (this role's
  // history). Skip 2026-06-22.
  const roleSections = useMemo(() => {
    if (!selectedEngagement) return null;
    // A PREVIOUS role has no live obligations — if someone else now holds it,
    // its open tasks aren't yours (Skip 2026-06-24). Suppress Current/Upcoming
    // entirely; only the Completed history (tasks you did while in the role)
    // shows below.
    const isPrev = !!selectedEngagement.isPrevious;
    // needs_response counts as "current" — an event awaiting your answer is
    // actionable now, not upcoming (surfaced by the chunk-A notice rows).
    const dueNow = (s: ActionStatus) =>
      s === "due" || s === "overdue" || s === "needs_response";
    const current = isPrev ? [] : railFilteredItems.filter((it) => dueNow(it.status));
    // Resolved rows (visible Getting-started checkmarks) are neither current
    // nor upcoming — they're finished.
    const upcoming = isPrev
      ? []
      : railFilteredItems.filter((it) => !dueNow(it.status) && it.status !== "resolved");
    // Materialized-but-not-yet-due rhythm instances for THIS role — the forward
    // look-ahead the global actionable list deliberately hides.
    //
    // Role rhythms are a COMMUNITY-ROLE concept (DEV-1224) and belong ONLY to the
    // community role they're assigned to — never to a project role that merely
    // shares the owning community, and never to a sibling community role (Skip
    // 2026-06-23). So: (a) only run for community/org roles, and (b) match the
    // instance's OWN role label (parsed from sourceLabel "Community — Role"), so
    // Secretary's rhythms don't leak onto Treasurer or onto a project role.
    const cg =
      !isPrev && selectedEngagement.source.type === "community" && selectedEngagement.groupCommunityGuid
        ? selectedEngagement.groupCommunityGuid.toUpperCase()
        : null;
    if (cg) {
      const selRole = selectedEngagement.roleLabel.trim().toLowerCase();
      const selSlot = (selectedEngagement.source.data as any)?.roleSlotGuid
        ? String((selectedEngagement.source.data as any).roleSlotGuid).toUpperCase()
        : null;
      // Upcoming shows only the NEXT iteration of each recurring rhythm, not
      // every future iteration (Skip 2026-06-24). Collect this role's matching
      // pending instances and keep the earliest-due one per rhythm task.
      const nextByTask = new Map<string, (typeof instances)[number]>();
      const whenOf = (i: (typeof instances)[number]) => i.dueAt || i.periodAnchor || "9999";
      for (const inst of instances) {
        if (inst.status !== "pending") continue;
        // Prefer the STABLE slot GUID — the role NAME breaks when the slot is
        // renamed (Skip 2026-06-24). Fall back to community + label for
        // legacy/non-slot instances.
        const instSlot = inst.roleSlotGuid ? String(inst.roleSlotGuid).toUpperCase() : null;
        if (selSlot && instSlot) {
          if (instSlot !== selSlot) continue;
        } else {
          if ((inst.communityEntityGuid || "").toUpperCase() !== cg) continue;
          const sl = inst.sourceLabel || "";
          const si = sl.indexOf(" — ");
          const instRole = si >= 0 ? sl.slice(si + 3).trim().toLowerCase() : "";
          if (instRole && selRole && instRole !== selRole) continue;
        }
        const key = inst.taskGuid || inst.instanceGuid;
        const prev = nextByTask.get(key);
        if (!prev || whenOf(inst) < whenOf(prev)) nextByTask.set(key, inst);
      }
      for (const inst of nextByTask.values()) {
        const item = rhythmToActionItem(inst, { t: (k: string) => t(k), now });
        // Pending = materialized but not yet in its lead window → not due. The
        // converter defaults to "due" (yellow); force on-track (green) so
        // Upcoming reads as future, not actionable-now (Skip, 2026-06-22).
        item.status = "ongoing";
        item.overdue = false;
        upcoming.push(item);
      }
    }
    // This role's completed history (best-effort match on role label / community).
    const completed = doneRows.filter((dr) => {
      const rl = (dr.role || "").trim();
      return (
        (rl !== "" && rl === selectedEngagement.roleLabel) ||
        dr.source === selectedEngagement.groupCommunityName ||
        dr.source === selectedEngagement.entityName
      );
    });
    return { current, upcoming, completed };
  }, [selectedEngagement, railFilteredItems, instances, doneRows, t, now]);

  // The open item for the tray. Searches the main list, then the selected
  // role's Current/Upcoming sections (the Upcoming pending cadence items are
  // synthesized and not in the main list — without this the tray came up empty).
  const selected = useMemo(
    () =>
      visibleItems.find((it) => it.id === selId) ||
      roleSections?.current.find((it) => it.id === selId) ||
      roleSections?.upcoming.find((it) => it.id === selId) ||
      doneSnapshots.get(selId || "") ||
      null,
    [visibleItems, roleSections, doneSnapshots, selId],
  );

  // Team roster machinery RELOCATED with the About-this-Role view to the
  // Memberships tray (Track 4 Slices C/D) — the role detail here is
  // obligations-only (Assigned tasks + Digest).


  // ── Resolution + undo ──
  // Seen/resolved/done state itself lives in the shared store
  // (actionItemSeenStore) — hydration (LS cache + authoritative server
  // seen-keys, DEV-1209) and persistence happen there, once, for BOTH this
  // list and the LeftNav badge. Here we only wrap the store mutations with
  // the inbox's local UI concerns (undo toast, row deselect).
  const markResolved = useCallback(
    (item: ActionItem, label: string) => {
      storeMarkResolved(item);
      setUndo({ id: item.id, label });
      setSelId(null);
    },
    [storeMarkResolved],
  );

  useEffect(() => {
    if (!undo) return;
    const tmr = window.setTimeout(() => setUndo(null), 9000);
    return () => window.clearTimeout(tmr);
  }, [undo]);

  // Completed/Activity history for the Sent view (DEV-1209). Server-side union
  // of the caller's finished work; merged with awaiting outbound workflows in
  // the sentRows memo below.
  useEffect(() => {
    let cancelled = false;
    fetchCompletedItems()
      .then((rows) => { if (!cancelled) setCompleted(rows || []); })
      .catch(() => { /* offline / endpoint not yet deployed — Sent shows awaiting only */ });
    return () => { cancelled = true; };
  }, []);

  const doUndo = useCallback(() => {
    if (!undo) return;
    unresolve(undo.id);
    setUndo(null);
  }, [undo, unresolve]);

  // ── Event handlers (mirror ContentApprovalsPage workflow plumbing) ──
  const me = String(currentUserGuid || "");

  const resolveEventApprove = useCallback(
    (item: ActionItem, note: string, label: string) => {
      const w = item.workflow;
      if (!w) return;
      setBusy(true);
      try {
        const next = approveRequest(w, me);
        if (note && next.history && next.history.length) {
          next.history[next.history.length - 1] = {
            ...next.history[next.history.length - 1],
            note,
          };
        }
        const applied = applyApprovedWorkflowSideEffects(next, me);
        if (!applied.ok) {
          window.alert(applied.error || t("contentApprovals.workflowError"));
          return;
        }
        addOrUpdateWorkflow(next);
        markResolved(item, label);
      } finally {
        setBusy(false);
      }
    },
    [me, t, markResolved],
  );

  const resolveEventReject = useCallback(
    (item: ActionItem, note: string, label: string) => {
      const w = item.workflow;
      if (!w) return;
      setBusy(true);
      try {
        const next = rejectRequest(w, me, note);
        applyRejectedWorkflowSideEffects(next, me);
        addOrUpdateWorkflow(next);
        markResolved(item, label);
      } finally {
        setBusy(false);
      }
    },
    [me, markResolved],
  );

  const handlers: ActionItemTrayHandlers = useMemo(
    () => ({
      acceptInvitation: (item, note) =>
        resolveEventApprove(item, note, t("actionCenter.inbox.resolved.accepted")),
      declineInvitation: (item, note) =>
        resolveEventReject(item, note, t("actionCenter.inbox.resolved.declined")),
      approveEvent: (item) =>
        resolveEventApprove(item, "", t("actionCenter.inbox.resolved.approved")),
      rejectEvent: (item) =>
        resolveEventReject(item, "", t("actionCenter.inbox.resolved.rejected")),
      acknowledge: (item) => {
        // Voice-ladder — "Read this: community standards". No bare
        // acknowledge: the button opens the read-to-accept modal (the
        // content), whose Accept records the versioned acknowledgment.
        if (item.checklistKey === "platform_standards") {
          setStandardsModalOpen(true);
          return;
        }
        // "Read me first" — opens the welcome screen (the content); closing
        // it from this path records the ack flag (see modal onClose below).
        if (item.checklistKey === "read_me_first") {
          setWelcomeModalOpen(true);
          return;
        }
        // Org-guidelines rows (both variants, Skip 2026-07-08): no bare
        // acknowledge — the button opens the CONTENT in-task (read-to-
        // accept modal, mirroring the standards act). The modal's Accept
        // performs the versioned ack; the org About page stays the durable
        // home of the material (§7.9 pointer discipline, amended to allow
        // the in-task content view).
        if (
          item.checklistKey === "org_guidelines" ||
          (item.checklistKey === "org_guidelines_member" && item.communityGuid)
        ) {
          setGuidelinesModalItem(item);
          return;
        }
        // §7.7 r2 — the Contributor celebration Notice. Viewing IS the
        // acknowledgment (Skip 2026-07-08, mirrors read_me_first): the
        // button opens the Contributor welcome popup, and the ack flag is
        // recorded alongside so the Notice retires to Done — where its
        // tray keeps the text and can re-open the popup forever.
        if (item.checklistKey === "promotion_notice") {
          window.dispatchEvent(new CustomEvent("l4e:show-contributor-welcome"));
          recordOnboardingMilestone({ milestoneKey: "contributor_promotion_ack" })
            .then(() => {
              markResolved(item, t("actionCenter.inbox.resolved.acknowledged"));
            })
            .catch(() => {
              /* popup already shown; flag write retries on next click */
            });
          return;
        }
        const w = item.workflow;
        if (!w) return;
        const at = new Date().toISOString();
        addOrUpdateWorkflow({
          ...w,
          status: "acknowledged",
          history: [...(w.history || []), { action: "acknowledge", by: me, at }],
        });
        markResolved(item, t("actionCenter.inbox.resolved.acknowledged"));
      },
      completeRhythm: (item) => {
        const inst = item.instance;
        if (!inst) return;
        setBusy(true);
        completeRoleRhythmInstance(inst.instanceGuid)
          .then(() => {
            refetchRhythms();
            markResolved(
              item,
              item.kind === "standing"
                ? t("actionCenter.inbox.resolved.logged")
                : t("actionCenter.inbox.resolved.done"),
            );
          })
          .catch((e) => window.alert(e instanceof Error ? e.message : String(e)))
          .finally(() => setBusy(false));
      },
      completeRaci: (item) => {
        // Mark a Responsible Gantt task done from the do-list tray (DEV-1368).
        // Sets % to 100 via the same patch the Digest uses, then invalidates
        // the project's gantt cache so useMyRaciTasks recomputes (the task
        // becomes done → drops out of the active list).
        const rt = item.raciTask;
        if (!rt) return;
        setBusy(true);
        patchGanttTaskProgress(rt.taskGuid, 100)
          .then(() => {
            invalidateAndRefetch(`ganttTasks:${rt.projectGuid}`);
            markResolved(item, t("actionCenter.inbox.resolved.done"));
          })
          .catch((e) => window.alert(e instanceof Error ? e.message : String(e)))
          .finally(() => setBusy(false));
      },
      snoozeRhythm: (item, days) => {
        const inst = item.instance;
        if (!inst) return;
        setBusy(true);
        snoozeRoleRhythmInstance(inst.instanceGuid, days)
          .then(() => {
            refetchRhythms();
            setSelId(null);
          })
          .catch((e) => window.alert(e instanceof Error ? e.message : String(e)))
          .finally(() => setBusy(false));
      },
      // DEV-1257. One-click Done on a mini-project task. Per the Q3 = (a)
      // + Option (b) locks: jumps straight to status='done' AND atomically
      // attaches any newly-uploaded evidence keys. Server folds new keys
      // into the existing array, then enforces RequiresEvidence on the
      // combined count. The upload itself happens inside the tray
      // (handleMpTaskDone in ActionItemTray); we only see the resulting
      // storage keys here.
      completeMpTask: (item, note, newEvidenceKeys) => {
        const row = item.mpTask;
        if (!row) return;
        setBusy(true);
        transitionMiniProjectTaskStatus(
          row.task.miniProjectGuid,
          row.task.taskGuid,
          "done",
          note,
          newEvidenceKeys,
        )
          .then(() => {
            refetchMpTasks();
            markResolved(item, t("actionCenter.inbox.resolved.done"));
          })
          .catch((e) => window.alert(e instanceof Error ? e.message : String(e)))
          .finally(() => setBusy(false));
      },
      // DEV-1257-followup. Upload-on-pick. The tray uploads files via
      // FileStorageController, then calls this handler with the resulting
      // storage keys to persist them on the task. Returns a Promise so
      // the tray can await + clear its in-flight indicator on completion.
      addMpTaskEvidence: async (item, keys) => {
        const row = item.mpTask;
        if (!row || keys.length === 0) return;
        try {
          await addMiniProjectTaskEvidence(
            row.task.miniProjectGuid,
            row.task.taskGuid,
            keys,
          );
          refetchMpTasks();
        } catch (e) {
          window.alert(e instanceof Error ? e.message : String(e));
        }
      },
      // DEV-1257-followup. Remove an existing evidence attachment from an
      // mp_task in place. The tray confirms before calling; we just persist
      // and refetch so the chip disappears. Stays within the open tray
      // (no row resolution) — removing evidence is mid-flight editing, not
      // task completion.
      removeMpTaskEvidence: (item, key) => {
        const row = item.mpTask;
        if (!row) return;
        setBusy(true);
        removeMiniProjectTaskEvidence(
          row.task.miniProjectGuid,
          row.task.taskGuid,
          key,
        )
          .then(() => refetchMpTasks())
          .catch((e) => window.alert(e instanceof Error ? e.message : String(e)))
          .finally(() => setBusy(false));
      },
      openSystemCta: (item) => {
        // Mirror the legacy Tasks-tab CTA routing. Completion is system-
        // detected by the milestone evaluator, so no manual resolve here.
        const ms: any = item.milestone;
        if (!ms) return;
        // §7.7 r1 item 4 — "Meet your organization" is orientation, not a
        // verifiable act: following the CTA IS completing it. Fire-and-forget
        // the server-side flag, then route to the org feed.
        if (ms.checklistKey === "meet_org") {
          recordOnboardingMilestone({ milestoneKey: "org_orientation_done" }).catch(() => {});
        }
        if (ms.milestone === "accept_eula") {
          setEulaOpen(true);
          return;
        }
        if (ms.milestone === "enable_2fa") {
          // Architect directive 2026-07-10: launch the TotpSetup flow IN
          // PLACE (one enrollment experience at every JIT callsite) — no
          // Account Settings detour. TotpSetup invalidates the security
          // cache on confirm, so the task resolves without a reload.
          setTotpModalOpen(true);
          return;
        }
        if (ms.ctaRoute) {
          // ctaState rides navigation state (e.g. { initialTab: 'about' }
          // for the profile CTA — DEV-991 mechanism on UserProfilePage).
          navigate(ms.ctaRoute, ms.ctaState ? { state: ms.ctaState } : undefined);
          setSelId(null);
        }
      },
      openCommunity: (guid) => {
        navigate(`/communities/${guid}`);
        setSelId(null);
      },
      openProject: (guid) => {
        navigate(`/projects/${guid}`);
        setSelId(null);
      },
      // DEV-1414. Identity verification for membership join requests —
      // the approver opens the requester's profile before deciding.
      openProfile: (guid) => {
        navigate(`/profile/${guid}`);
        setSelId(null);
      },
      // DEV-1257. Canonical MP open route is /projects/{guid}/management —
      // see MiniProjectDetailPanel.handleViewFull. The `/mini-projects/`
      // namespace is being retired (Task #233). MPs have no parent-project
      // FK, but the route uses the MP's own guid in the `/projects/.../management`
      // shape because the destination page disambiguates (it tries MP first,
      // falls through to full Project on miss).
      openMiniProject: (guid) => {
        navigate(`/projects/${guid}/management`);
        setSelId(null);
      },
      openEngagement: () => {
        // Engagements merged into the Action Center (Slice F) — this is now a
        // same-page action; deep-linking to a specific role selector is a
        // later enhancement.
        navigate("/actions");
        setSelId(null);
      },
      onDataSaved: (info) => {
        onProjectConfigChanged?.();
        bumpVersion();
        // DEV-1256: only resolve the data item when this save covered EVERY
        // metric in the grid. A partial save (some elements still empty) must
        // keep the item open in To do — the old unconditional resolve marked
        // the whole project+section Done after entering a single element, and
        // the day-scoped resolved cache then locked the user out of finishing
        // it (Done is read-only by design). Partial saves leave the tray open
        // to continue; the refetch updates the counts, and when the remaining
        // elements are saved (or the data refresh shows nothing left due) the
        // item resolves or naturally leaves the list.
        const fullCoverage =
          !!info && info.totalCount > 0 && info.savedCount >= info.totalCount;
        if (!fullCoverage) return;
        const cur = selId ? items.find((i) => i.id === selId) : null;
        if (cur && cur.kind === "data") {
          markResolved(cur, t("actionCenter.inbox.resolved.saved"));
        }
      },
    }),
    [resolveEventApprove, resolveEventReject, me, markResolved, refetchRhythms, refetchMpTasks, onProjectConfigChanged, selId, items, t, navigate, recordOnboardingMilestone, bumpVersion],
  );

  // ── Header sort ──
  const onSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
    setSelId(null);
  };

  const caret = (k: SortKey) =>
    sortKey === k ? (sortDir === 1 ? " ▲" : " ▼") : "";

  const openRow = (id: string) => {
    setSelId((cur) => (cur === id ? null : id));
    // Opening a row = "I'm aware of it" → mark seen (unbolds). Optimistic local
    // update + fire-and-forget server persist (idempotent; DEV-1209).
    markSeen([id]);
  };

  // Single inbox row — reused by the flat list and by each grouped section so
  // the row markup stays in one place (SPEC_ROLE_ORGANIZED_INBOX §4a).
  const renderItemRow = (it: ActionItem) => {
    const handled = it.status === "resolved";
    const unseen = !handled && !seenIds.has(it.id);
    const statusClass =
      it.status === "overdue"
        ? styles.statusCritical
        : it.status === "due" || it.status === "needs_response"
          ? styles.statusWarning
          : it.status === "ongoing"
            ? styles.statusOnTrack
            : styles.statusNeutral;
    return (
      <div
        key={it.id}
        className={`${styles.row} ${styles.colsInbox} ${unseen ? statusClass : ""} ${selId === it.id ? styles.rowSel : ""} ${handled ? styles.rowDone : ""} ${unseen ? styles.unseen : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => openRow(it.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openRow(it.id);
          }
        }}
      >
        <span className={styles.indCell}>
          {handled ? <i className={`ti ti-check ${styles.chk}`} aria-hidden="true" /> : null}
        </span>
        {/* DEV-1407 Ask 2 — the .colType / .colDue / .colStatus markers drive
            progressive column hiding as the container narrows (drop order:
            due date → status → type). Every hidden value is reachable in the
            tray, which is the ONLY reason hiding is acceptable. The "Added"
            date carries no marker and never drops: it is the one value the
            tray does NOT carry. */}
        <span className={`${styles.typeChip} ${styles.colType}`}>{t(it.typeKey)}</span>
        <span className={styles.sourceCell} title={it.source}>{it.source}</span>
        <span className={styles.sourceCell} title={it.role || ""}>{it.role || "—"}</span>
        <span className={styles.subject}>{it.subject}</span>
        <span className={styles.dateCell}>{it.addedLabel || "—"}</span>
        <span className={`${styles.dateCell} ${styles.colDue} ${it.overdue ? styles.dateOver : ""}`}>{it.dateLabel}</span>
        <span className={`${styles.statusCell} ${styles.colStatus}`}>
          <span className={`${styles.status} ${STATUS_CLASS[it.status]}`}>{t(statusKey(it.status))}</span>
        </span>
      </div>
    );
  };

  // Shared inbox header row (sortable). Used above both the flat list and the
  // grouped sections so column alignment is identical.
  const inboxHeader = (
    <div className={`${styles.headerRow} ${styles.colsInbox}`}>
      <span className={styles.headerCell} aria-hidden="true" />
      <button type="button" className={`${styles.headerCell} ${styles.colType} ${sortKey === "type" ? styles.headerCellActive : ""}`} onClick={() => onSort("type")}>
        {t("actionCenter.inbox.col.type")}{caret("type")}
      </button>
      <button type="button" className={`${styles.headerCell} ${sortKey === "source" ? styles.headerCellActive : ""}`} onClick={() => onSort("source")}>
        {t("actionCenter.inbox.col.source")}{caret("source")}
      </button>
      <button type="button" className={`${styles.headerCell} ${sortKey === "role" ? styles.headerCellActive : ""}`} onClick={() => onSort("role")}>
        {t("actionCenter.inbox.col.role")}{caret("role")}
      </button>
      <button type="button" className={`${styles.headerCell} ${sortKey === "priority" ? styles.headerCellActive : ""}`} onClick={() => onSort("priority")}>
        {t("actionCenter.inbox.col.subject")}{caret("priority")}
      </button>
      {/* "Added" — never dropped (the one value not in the tray). */}
      <button type="button" className={`${styles.headerCell} ${styles.headerCellCenter} ${sortKey === "added" ? styles.headerCellActive : ""}`} onClick={() => onSort("added")}>
        {t("actionCenter.inbox.col.added")}{caret("added")}
      </button>
      <button type="button" className={`${styles.headerCell} ${styles.headerCellCenter} ${styles.colDue} ${sortKey === "date" ? styles.headerCellActive : ""}`} onClick={() => onSort("date")}>
        {t("actionCenter.inbox.col.dueDate")}{caret("date")}
      </button>
      <button type="button" className={`${styles.headerCell} ${styles.headerCellCenter} ${styles.colStatus} ${sortKey === "status" ? styles.headerCellActive : ""}`} onClick={() => onSort("status")}>
        {t("actionCenter.inbox.col.status")}{caret("status")}
      </button>
    </div>
  );

  // Compact row for a selected role's task list — omits Source + Role columns
  // (the role/community are the context). Issue (6), Skip 2026-06-22.
  const renderRoleTaskRow = (it: ActionItem) => {
    const handled = it.status === "resolved";
    const unseen = !handled && !seenIds.has(it.id);
    const statusClass =
      it.status === "overdue"
        ? styles.statusCritical
        : it.status === "due" || it.status === "needs_response"
          ? styles.statusWarning
          : it.status === "ongoing"
            ? styles.statusOnTrack
            : styles.statusNeutral;
    return (
      <div
        key={it.id}
        className={`${styles.row} ${styles.colsRole} ${unseen ? statusClass : ""} ${selId === it.id ? styles.rowSel : ""} ${handled ? styles.rowDone : ""} ${unseen ? styles.unseen : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => openRow(it.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openRow(it.id);
          }
        }}
      >
        <span className={styles.indCell}>
          {handled ? <i className={`ti ti-check ${styles.chk}`} aria-hidden="true" /> : null}
        </span>
        <span className={styles.typeChip}>{t(it.typeKey)}</span>
        <span className={styles.subject}>{it.subject}</span>
        <span className={styles.dateCell}>{it.addedLabel || "—"}</span>
        <span className={`${styles.dateCell} ${it.overdue ? styles.dateOver : ""}`}>{it.dateLabel}</span>
        <span className={styles.statusCell}>
          <span className={`${styles.status} ${STATUS_CLASS[it.status]}`}>{t(statusKey(it.status))}</span>
        </span>
      </div>
    );
  };

  // Compact completed row for a role's "Completed" section.
  const renderRoleDoneRow = (dr: DoneRow) => (
    <div
      key={dr.id}
      className={`${styles.row} ${styles.colsRole} ${styles.rowDone} ${selId === dr.id ? styles.rowSel : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => openRow(dr.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openRow(dr.id);
        }
      }}
    >
      <span className={styles.indCell} aria-hidden="true" />
      <span className={styles.typeChip}>{t(dr.typeKey)}</span>
      <span className={styles.subject}>
        {dr.subject}
        {dr.count && dr.count > 1 ? ` (${dr.count})` : ""}
      </span>
      <span className={styles.dateCell}>{dr.addedLabel}</span>
      <span className={styles.dateCell}>{dr.dateLabel}</span>
      <span className={styles.statusCell}>
        <span className={`${styles.status} ${DONE_STATUS_CLASS[dr.status]}`}>
          {t(`actionCenter.inbox.doneStatus.${dr.status}`)}
        </span>
      </span>
    </div>
  );

  const roleTasksHeader = (
    <div className={`${styles.headerRow} ${styles.colsRole}`}>
      <span className={styles.headerCell} aria-hidden="true" />
      <button type="button" className={`${styles.headerCell} ${sortKey === "type" ? styles.headerCellActive : ""}`} onClick={() => onSort("type")}>
        {t("actionCenter.inbox.col.type")}{caret("type")}
      </button>
      <button type="button" className={`${styles.headerCell} ${sortKey === "priority" ? styles.headerCellActive : ""}`} onClick={() => onSort("priority")}>
        {t("actionCenter.inbox.col.subject")}{caret("priority")}
      </button>
      <button type="button" className={`${styles.headerCell} ${styles.headerCellCenter} ${sortKey === "added" ? styles.headerCellActive : ""}`} onClick={() => onSort("added")}>
        {t("actionCenter.inbox.col.added")}{caret("added")}
      </button>
      <button type="button" className={`${styles.headerCell} ${styles.headerCellCenter} ${sortKey === "date" ? styles.headerCellActive : ""}`} onClick={() => onSort("date")}>
        {t("actionCenter.inbox.col.dueDate")}{caret("date")}
      </button>
      <button type="button" className={`${styles.headerCell} ${styles.headerCellCenter} ${sortKey === "status" ? styles.headerCellActive : ""}`} onClick={() => onSort("status")}>
        {t("actionCenter.inbox.col.status")}{caret("status")}
      </button>
    </div>
  );

  const railAvatar = (label: string, pillar: RolePillar, icon?: string) => (
    <span className={`${styles.railAvatar} ${PILLAR_CLASS[pillar]}`} aria-hidden="true">
      {icon ? <i className={`ti ${icon}`} /> : pillar === "base" ? "L4" : initialsOf(label)}
    </span>
  );

  // Count must match what the All Actions list actually shows: drop both
  // session-resolved items AND status "resolved" (e.g. completed Gantt R
  // tasks), which visibleItems hides. Otherwise the badge over-counts
  // (36 vs 11) — Skip 2026-06-23.
  const inboxCount = items.filter(
    (it) => !resolvedIds.has(it.id) && it.status !== "resolved",
  ).length;
  const unseenCount = items.filter(
    (it) => !resolvedIds.has(it.id) && it.status !== "resolved" && !seenIds.has(it.id),
  ).length;

  // Toolbar (To do/Done · search · Unseen/Seen). Moved INSIDE the content pane
  // (railMain / Done list) per Skip — it sits above the actions list, not in a
  // bar spanning the rail.
  const toolbar = (
    <div className={styles.toolbar}>
      {/* To do/Done apply to the cross-role list only; a selected role has its
          own Current/Upcoming/Completed sections (Skip, 2026-06-22). */}
      {!selectedEngagement && (
        <div className={styles.seg} role="tablist">
          <button
            type="button"
            className={`${styles.segBtn} ${view === "todo" ? styles.segBtnOn : ""}`}
            onClick={() => {
              setView("todo");
              setSelId(null);
            }}
          >
            {t("actionCenter.inbox.tabToDo")}
            {inboxCount > 0 ? ` · ${inboxCount}` : ""}
          </button>
          <button
            type="button"
            className={`${styles.segBtn} ${view === "done" ? styles.segBtnOn : ""}`}
            onClick={() => {
              setView("done");
              setSelId(null);
            }}
          >
            {t("actionCenter.inbox.tabDone")}
          </button>
        </div>
      )}

      {/* The inbox search is a LIST-level control: it filters the master
          action-item list (subject / source / type). Hidden once a role is
          selected — mirroring the read filter below, which already does this.
          Leaving it visible in the role view was misleading (it never filtered
          Digest or About this Role) and actively harmful: a query left over from
          All Actions silently filtered that role's Assigned Tasks, so the role
          looked like it had fewer tasks than it does. Skip 2026-07-10. */}
      {!selectedEngagement && (
        <div className={styles.searchWrap}>
          <i className={`ti ti-search ${styles.searchIcon}`} aria-hidden="true" />
          <input
            className={styles.searchInput}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("actionCenter.inbox.searchPlaceholder")}
            aria-label={t("actionCenter.inbox.searchPlaceholder")}
          />
        </div>
      )}

      {view === "todo" && !selectedEngagement && (
        <div className={styles.fseg}>
          {(["all", "unseen", "seen"] as const).map((k) => (
            <button
              key={k}
              type="button"
              className={`${styles.fsegBtn} ${readFilter === k ? styles.fsegOn : ""}`}
              onClick={() => { setReadFilter(k); setSelId(null); }}
            >
              {t(`actionCenter.inbox.read.${k}`)}
              {k === "unseen" && unseenCount > 0 ? ` (${unseenCount})` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    /* DEV-1407 — LANE LAYOUT. The rail was previously nested INSIDE the
       bordered .surface card, which itself sits inside an 18px-padded page:
       that nesting is precisely why it read as "undocked" next to the My
       Impact rail. It now sits at the lane level as the shell's rails do, so
       it is flush against LeftNav BY CONSTRUCTION rather than by a margin
       someone has to remember. The page wrapper drops its padding for this
       branch (.pageFlush in ContentApprovalsPage.module.css) and .inboxMain
       re-applies it, so the main column looks unchanged. */
    <div className={styles.root}>
      {/* Rail — entity-grouped role navigator (SPEC_ROLE_ORGANIZED_INBOX §4a),
          now on the SHARED Rail primitive: one chrome, one collapse behavior,
          one breakpoint, one --rail-* token family. Minimize persists under
          l4e_rail_action_center_collapsed; collapsed it still reads
          "Actions & Roles" via the primitive's collapsedLabel.

          §7.12 / DEV-1436 first-run disclosure is UNCHANGED: hidden when there
          is nothing to organize (<2 obligation groupings — the belonging
          "Life4Earth {rung}" identity group does NOT count). Hide the rail,
          never the content; the first appearance pulses as an unlock, not a
          layout jump (the pulse rides the primitive's className slot). */}
      {railVisible && (
        <Rail
          ariaLabel={t("actionCenter.inbox.rail.ariaLabel")}
          widthVariant="list"
          persistKey="l4e_rail_action_center_collapsed"
          collapsedLabel={t("actionCenter.inbox.rail.collapsedLabel")}
          className={railRevealPulse ? styles.railReveal : undefined}
          /* Skip 2026-07-10: All Actions rides the primitive's headerLabel slot,
             so it leads the rail AND the collapse toggle — which always sits at
             the header's inline-end — lands directly to its RIGHT. The empty
             header row is reclaimed.

             Why the header and not the body: the toggle MUST stay in the header.
             .railContent is hidden when the rail collapses while the header (and
             its toggle) survives — put the toggle beside All Actions in the body
             and a collapsed rail could never be re-expanded. This uses the
             sanctioned headerLabel slot rather than forking the shared chrome
             (POLICY_NAVIGATION_RAILS §3). The wrapper neutralizes .headerLabel's
             uppercase label typography, which would otherwise leak in. */
          headerLabel={
            <span className={styles.railHeaderSlot}>
              <button
                type="button"
                className={`${styles.railAllActions} ${railSel === "all" ? styles.railAllActionsOn : ""}`}
                aria-pressed={railSel === "all"}
                onClick={() => { setRailSel("all"); setSelId(null); }}
              >
                <span className={styles.railAllAvatar} aria-hidden="true">
                  <i className="ti ti-world" />
                </span>
                <span className={styles.railAllLabel}>{t("actionCenter.inbox.rail.allActions")}</span>
                <span className={styles.railAllCount}>{inboxCount}</span>
              </button>
            </span>
          }
        >
          {/* Collapsed, the rail is a 56px strip carrying only the vertical
              "Actions & Roles" label — these text-bearing rows would render
              crushed inside it. Hidden via the primitive's data-collapsed
              marker (the same mechanism Verification's .railViewport uses). */}
          <div className={styles.railContent}>
              {/* Shared RailSearch (DEV-1476). All Actions now leads the rail from
                  the header slot above (with the collapse toggle at its right), so
                  the filter sits here in the body — directly above the roles list
                  it actually filters. It never filtered All Actions or Invitations,
                  which are pinned. */}
              <RailSearch
                value={railQuery}
                onChange={setRailQuery}
                placeholder={t("actionCenter.inbox.rail.filterPlaceholder")}
              />
              {railModel.invitations.length > 0 && (
                <button
                  type="button"
                  className={`${styles.railInvites} ${railSel === "inv" ? styles.railInvitesOn : ""}`}
                  aria-pressed={railSel === "inv"}
                  onClick={() => { setRailSel("inv"); setSelId(null); }}
                >
                  <i className={`ti ti-mail ${styles.railInvitesIcon}`} aria-hidden="true" />
                  <span className={styles.railInvitesLabel}>{t("actionCenter.inbox.rail.invitations")}</span>
                  <span className={styles.railInvitesBadge}>{railModel.invitations.length}</span>
                </button>
              )}
              <div className={styles.railSecLabel}>{t("actionCenter.inbox.rail.yourRoles")}</div>
              {/* The Life4Earth role #0 is folded into the "Personal Roles" home
                  group below (rendered as its first role), so there's no separate
                  base row — the home is the user's pinned, always-present node. */}
              {engagementCommunities
                .filter(
                  (c) =>
                    matchesRail(c.name) ||
                    c.entries.some(
                      ({ e }) => matchesRail(e.roleLabel) || matchesRail(e.entityName),
                    ),
                )
                .map((c) => {
                  const open = expandedRail.has(c.guid);
                  const shown = c.entries.filter(
                    ({ e }) =>
                      matchesRail(c.name) ||
                      matchesRail(e.roleLabel) ||
                      matchesRail(e.entityName),
                  );
                  // Single-role groups have no expander — clicking selects that
                  // one role directly (Skip 2026-06-23). The "Personal Roles"
                  // home folds the Life4Earth role #0 in as an implicit first
                  // role, so its count is entries + 1.
                  const isHome = c.guid === "workshop";
                  const baseOverdue = isHome
                    ? railModel.base.some((it) => it.status === "overdue")
                    : false;
                  const roleCount = c.entries.length + (isHome ? 1 : 0);
                  const single = roleCount === 1;
                  const headerCount = isHome ? c.total + railModel.base.length : c.total;
                  const headerOverdue = c.overdue || baseOverdue;
                  const onlyKey = !isHome && single ? c.entries[0].e.key : null;
                  const headerSelected =
                    single && (isHome ? railSel === ROLE_BASE_KEY : railSel === `eng:${onlyKey}`);
                  const selectSingle = () => {
                    if (isHome) {
                      setRailSel(ROLE_BASE_KEY);
                    } else {
                      setRailSel(`eng:${onlyKey}`);
                      setRoleTab("tasks");
                      setShowUpcoming(false);
                      setShowCompleted(false);
                    }
                    setSelId(null);
                  };
                  return (
                    <div key={c.guid}>
                      <button
                        type="button"
                        className={`${styles.railComm} ${headerSelected ? styles.railCommOn : ""}`}
                        onClick={() => (single ? selectSingle() : toggleRailGroup(c.guid))}
                        aria-expanded={single ? undefined : open}
                        aria-pressed={single ? headerSelected : undefined}
                      >
                        <span className={styles.railCommAvatar} aria-hidden="true">
                          {c.flagEmoji ? (
                            <span className={styles.railCommFlag}>{c.flagEmoji}</span>
                          ) : c.logoUrl ? (
                            <img src={c.logoUrl} alt="" loading="lazy" />
                          ) : c.guid.startsWith("workshop") ? (
                            <i className="ti ti-user" />
                          ) : (
                            <span className={styles.railCommMono}>{initialsOf(c.name)}</span>
                          )}
                        </span>
                        <span className={styles.railCommText}>
                          <span className={styles.railCommName} title={c.name}>{c.name}</span>
                          {c.subtitle ? (
                            <span className={styles.railCommSub}>{c.subtitle}</span>
                          ) : null}
                        </span>
                        {headerOverdue && (
                          <span
                            className={styles.railPip}
                            title={t("actionCenter.inbox.rail.hasOverdue")}
                          />
                        )}
                        {headerCount > 0 && <span className={styles.railCount}>{headerCount}</span>}
                        {!single && (
                          <i
                            className={`ti ti-chevron-${open ? "down" : "right"} ${styles.railCommChev}`}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                      {!single && open && isHome && (
                        <button
                          type="button"
                          className={`${styles.railRole} ${railSel === ROLE_BASE_KEY ? styles.railRoleOn : ""}`}
                          onClick={() => { setRailSel(ROLE_BASE_KEY); setSelId(null); }}
                        >
                          <span
                            className={styles.railRoleDot}
                            style={{ background: "var(--pillar-solution, #1D9E75)" }}
                            aria-hidden="true"
                          />
                          <span className={styles.railRoleLabel}>{baseRoleLabel}</span>
                          {railModel.base.length > 0 && (
                            <span className={styles.railCount}>{railModel.base.length}</span>
                          )}
                        </button>
                      )}
                      {!single &&
                        open &&
                        shown.map(({ e, count, overdue }) => (
                          <button
                            key={e.key}
                            type="button"
                            className={`${styles.railRole} ${railSel === `eng:${e.key}` ? styles.railRoleOn : ""} ${e.isPrevious ? styles.railRolePrev : ""}`}
                            onClick={() => {
                              setRailSel(`eng:${e.key}`);
                              setRoleTab("tasks");
                              setShowUpcoming(false);
                              setShowCompleted(false);
                              setSelId(null);
                            }}
                          >
                            <span
                              className={styles.railRoleDot}
                              style={{ background: PILLAR_CSS_TOKEN[e.pillar] }}
                              aria-hidden="true"
                            />
                            <span className={styles.railRoleLabel} title={e.roleLabel}>
                              {e.roleLabel}
                            </span>
                            {e.isPrevious && (
                              <span
                                className={styles.railEndedTag}
                                title={
                                  e.previousKind === "project_ended"
                                    ? t("actionCenter.inbox.rail.endedProjectHint")
                                    : t("actionCenter.inbox.rail.endedRoleHint")
                                }
                              >
                                {e.previousKind === "project_ended"
                                  ? t("actionCenter.inbox.rail.endedProject")
                                  : t("actionCenter.inbox.rail.endedRole")}
                              </span>
                            )}
                            {count > 0 && (
                              <span
                                className={`${styles.railCount} ${overdue ? styles.railCountOver : ""}`}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        ))}
                    </div>
                  );
                })}
              {railModel.groups
                .filter((g) => g.key === ROLE_OTHER_KEY)
                .map((g) => (
                  <button
                    key="other"
                    type="button"
                    className={`${styles.railEntry} ${railSel === ROLE_OTHER_KEY ? styles.railEntryOn : ""}`}
                    onClick={() => { setRailSel(ROLE_OTHER_KEY); setSelId(null); }}
                  >
                    {railAvatar(g.label, "other")}
                    <span className={styles.railEntryLabel}>{t("actionCenter.inbox.rail.other")}</span>
                    <span className={styles.railCount}>{g.items.length}</span>
                  </button>
                ))}
              {/* Sticky footer — reveal previous (ended/closed) roles. */}
              <button
                type="button"
                className={`${styles.railPrevToggle} ${showPrevious ? styles.railPrevToggleOn : ""}`}
                aria-pressed={showPrevious}
                onClick={() => setShowPrevious((v) => !v)}
              >
                <i className={`ti ti-history ${styles.railPrevIcon}`} aria-hidden="true" />
                <span>
                  {showPrevious
                    ? t("actionCenter.inbox.rail.hidePrevious")
                    : t("actionCenter.inbox.rail.showPrevious")}
                </span>
              </button>
          </div>
        </Rail>
      )}

      {/* Main column — carries the page padding the flush wrapper gave up, so
          the bordered surface card reads exactly as before beside the now-
          docked rail. */}
      <div className={styles.inboxMain}>
        {/* Surface */}
        <div
          className={styles.surface}
          ref={surfaceRef}
          style={surfaceH ? { blockSize: surfaceH } : undefined}
        >
            {/* Detail pane — All Actions (grouped/flat) or one role's items */}
            <div className={styles.railMain}>
              {toolbar}
              {view === "done" && !selectedEngagement ? (
                <>
                  <div className={`${styles.headerRow} ${styles.colsSent}`}>
                    <span className={styles.headerCell}>{t("actionCenter.inbox.col.type")}</span>
                    <span className={styles.headerCell}>{t("actionCenter.inbox.col.source")}</span>
                    <span className={styles.headerCell}>{t("actionCenter.inbox.col.role")}</span>
                    <span className={styles.headerCell}>{t("actionCenter.inbox.col.subject")}</span>
                    <span className={`${styles.headerCell} ${styles.headerCellCenter}`}>{t("actionCenter.inbox.col.added")}</span>
                    <span className={`${styles.headerCell} ${styles.headerCellCenter}`}>{t("actionCenter.inbox.col.dueDate")}</span>
                    <span className={`${styles.headerCell} ${styles.headerCellCenter}`}>{t("actionCenter.inbox.col.dateCompleted")}</span>
                    <span className={`${styles.headerCell} ${styles.headerCellCenter}`}>{t("actionCenter.inbox.col.status")}</span>
                  </div>
                  <div className={styles.listScroll}>
                    {railFilteredDone.length === 0 ? (
                      <div className={styles.empty}>{t("actionCenter.inbox.emptyDone")}</div>
                    ) : (
                      railFilteredDone.map((s) => (
                        <div
                          key={s.id}
                          className={`${styles.row} ${styles.colsSent} ${selId === s.id ? styles.rowSel : ""}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => openRow(s.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openRow(s.id);
                            }
                          }}
                        >
                          <span className={styles.typeChip}>{t(s.typeKey)}</span>
                          <span className={styles.sourceCell} title={s.source}>{s.source}</span>
                          <span className={styles.sourceCell} title={s.role}>{s.role}</span>
                          <span className={styles.subject}>
                            {s.subject}
                            {s.count && s.count > 1 ? ` (${s.count})` : ""}
                          </span>
                          <span className={styles.dateCell}>{s.addedLabel}</span>
                          <span className={styles.dateCell}>{s.dueLabel}</span>
                          <span className={styles.dateCell}>{s.dateLabel}</span>
                          <span className={styles.statusCell}>
                            <span className={`${styles.status} ${DONE_STATUS_CLASS[s.status]}`}>
                              {t(`actionCenter.inbox.doneStatus.${s.status}`)}
                            </span>
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : selectedEngagement ? (
                <>
                  <div
                    className={styles.roleBanner}
                    style={
                      selectedEngagement.heroUrl
                        ? {
                            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%), url(${selectedEngagement.heroUrl})`,
                          }
                        : undefined
                    }
                  >
                    <div className={styles.roleBannerContent}>
                      <div className={styles.roleBannerRow}>
                        <div className={styles.roleTitlesBlock}>
                          {selectedEngagement.logoUrl ? (
                            <img className={styles.roleAvatar} src={selectedEngagement.logoUrl} alt="" />
                          ) : (
                            <div className={styles.roleAvatarFallback} aria-hidden="true">
                              {initialsOf(selectedEngagement.entityName || selectedEngagement.roleLabel)}
                            </div>
                          )}
                          <div className={styles.roleTitles}>
                            <div className={styles.roleBannerTitle}>{selectedEngagement.roleLabel}</div>
                            <div className={styles.roleBannerEntity}>{selectedEngagement.entityName}</div>
                            {/* Geo community path under the project name (moved
                                off the card). Each ancestor links to its own
                                community page; falls back to plain secondary
                                text if the chain can't be resolved. Skip 2026-06-22. */}
                            {(() => {
                              const owningGuid =
                                selectedEngagement.groupCommunityGuid ||
                                selectedEngagement.owningCommunityGuid ||
                                "";
                              const path = owningGuid ? resolveCommunityPath(owningGuid) : [];
                              if (path.length > 0) {
                                return (
                                  <div className={styles.roleBannerPath}>
                                    {path.map((hop, i) => (
                                      <React.Fragment key={hop.guid}>
                                        {i > 0 ? (
                                          <span className={styles.roleBannerPathSep} aria-hidden="true">
                                            {" / "}
                                          </span>
                                        ) : null}
                                        <button
                                          type="button"
                                          className={styles.roleBannerPathLink}
                                          onClick={() => navigate(`/communities/${hop.guid}`)}
                                        >
                                          {hop.name}
                                        </button>
                                      </React.Fragment>
                                    ))}
                                  </div>
                                );
                              }
                              return selectedEngagement.secondary ? (
                                <div className={styles.roleBannerPath}>{selectedEngagement.secondary}</div>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        {(() => {
                          // Context-aware action: project roles open the PROJECT,
                          // community/org roles open the community (Skip 2026-06-22).
                          const d = selectedEngagement.source.data as any;
                          const tag = selectedEngagement.typeTag;
                          let label: string | null = null;
                          let onClick: (() => void) | null = null;
                          if (tag === "full-project" || tag === "mini-project") {
                            const guid = d?._routeGuid || d?.projectGuid;
                            if (guid) {
                              label = t("actionCenter.inbox.roleTab.viewProject");
                              onClick = () =>
                                navigate(
                                  tag === "mini-project"
                                    ? `/projects/${guid}/management`
                                    : `/projects/${guid}`,
                                );
                            }
                          } else if (tag === "community" || tag === "organization") {
                            const guid = d?.entityGuid || selectedEngagement.groupCommunityGuid;
                            if (guid) {
                              label = t("actionCenter.inbox.roleTab.openCommunity");
                              onClick = () => navigate(`/communities/${guid}`);
                            }
                          } else if (selectedEngagement.source.type === "workspace") {
                            // Problem workspace role → open the workspace. Use the
                            // community-scoped route when the workspace has a
                            // community, else the standalone route.
                            const wg = d?.workspaceGuid;
                            const cg = d?.communityGuid;
                            if (wg) {
                              label = t("actionCenter.inbox.roleTab.viewWorkspace");
                              onClick = () =>
                                navigate(cg ? `/communities/${cg}/workspaces/${wg}` : `/workspaces/${wg}`);
                            }
                          }
                          // Track 4 Slice D (§7.1) — the compact context
                          // header's bridge to the role's REFERENCE view.
                          // Slice C′: a real, shareable route (main-pane role
                          // page) instead of one-shot router state.
                          return (
                            <div className={styles.roleBannerActions}>
                              <button
                                type="button"
                                className={styles.roleBannerBtn}
                                onClick={() =>
                                  navigate(
                                    `/memberships/role/${encodeURIComponent(selectedEngagement.key)}`,
                                  )
                                }
                              >
                                {t("actionCenter.inbox.roleTab.viewInMemberships")}
                              </button>
                              {label && onClick ? (
                                <button type="button" className={styles.roleBannerBtn} onClick={onClick}>
                                  {label}
                                </button>
                              ) : null}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  {(() => {
                    // Track 4 Slice D (§7.1 / DEV-1425): the "About this Role"
                    // tab RELOCATED to the Memberships tray — the role detail
                    // here is obligations-only. Full-project roles keep the
                    // RACI Digest tab (A/C/I oversight, Skip 2026-06-23);
                    // everything else has just Assigned tasks, so the tab bar
                    // only renders when there's a real choice.
                    const isFullProject =
                      selectedEngagement.typeTag === "full-project" &&
                      selectedEngagement.source.type === "role";
                    if (!isFullProject) return null;
                    return (
                      <div className={styles.roleTabs}>
                        {(["tasks", "digest"] as const).map((tab) => (
                          <button
                            key={tab}
                            type="button"
                            className={`${styles.roleTab} ${roleTab === tab ? styles.roleTabOn : ""}`}
                            onClick={() => setRoleTab(tab)}
                          >
                            {t(`actionCenter.inbox.roleTab.${tab}`)}
                            {tab === "tasks" && railFilteredItems.length > 0 ? (
                              <span className={styles.roleTabCount}>{railFilteredItems.length}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {roleTab === "tasks" ? (
                    <>
                      {roleTasksHeader}
                      <div className={styles.listScroll}>
                        {/* Current = due + overdue (always shown) */}
                        {roleSections && roleSections.current.length > 0 ? (
                          roleSections.current.map(renderRoleTaskRow)
                        ) : (
                          <div className={styles.empty}>{t("actionCenter.inbox.roleTab.noCurrent")}</div>
                        )}
                        {/* Upcoming — collapsed by default */}
                        {roleSections && roleSections.upcoming.length > 0 && (
                          <>
                            <button
                              type="button"
                              className={styles.roleSecHeader}
                              onClick={() => setShowUpcoming((v) => !v)}
                            >
                              <i className={`ti ti-chevron-${showUpcoming ? "down" : "right"}`} aria-hidden="true" />
                              <span>{t("actionCenter.inbox.roleTab.upcoming")}</span>
                              <span className={styles.sectionCount}>{roleSections.upcoming.length}</span>
                            </button>
                            {showUpcoming && roleSections.upcoming.map(renderRoleTaskRow)}
                          </>
                        )}
                        {/* Completed — collapsed by default */}
                        {roleSections && roleSections.completed.length > 0 && (
                          <>
                            <button
                              type="button"
                              className={styles.roleSecHeader}
                              onClick={() => setShowCompleted((v) => !v)}
                            >
                              <i className={`ti ti-chevron-${showCompleted ? "down" : "right"}`} aria-hidden="true" />
                              <span>{t("actionCenter.inbox.roleTab.completed")}</span>
                              <span className={styles.sectionCount}>{roleSections.completed.length}</span>
                            </button>
                            {showCompleted && roleSections.completed.map(renderRoleDoneRow)}
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className={styles.listScroll}>
                      {(() => {
                        // Full RACI briefing (R/A/C/I) for this full-project role —
                        // the real Engagements Role Digest, brought into the role
                        // detail so A/C/I oversight lives here, not in the do-list
                        // (Skip 2026-06-23). R appears here too (full picture).
                        // (Reachable only for full-project roles — the tab bar
                        // renders solely there; the About-this-Role branch moved
                        // to the Memberships tray, Track 4 Slice D.)
                        const d = selectedEngagement.source.data as any;
                        const pg = d?.projectGuid;
                        const slot = d?.roleSlotGuid;
                        return pg && slot ? (
                          <RoleDigestView
                            projectGuid={String(pg)}
                            roleSlotGuids={[String(slot)]}
                            currentUserGuid={currentUserGuid}
                          />
                        ) : (
                          <div className={styles.empty}>{t("actionCenter.inbox.roleTab.descEmpty")}</div>
                        );
                      })()}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {railSel === "all" && railVisible && (
                    <div className={styles.railGroupBy}>
                      <span className={styles.segLabel}>{t("actionCenter.inbox.rail.groupBy")}</span>
                      <div className={styles.fseg}>
                        <button
                          type="button"
                          className={`${styles.fsegBtn} ${groupMode === "role" ? styles.fsegOn : ""}`}
                          onClick={() => setGroupMode("role")}
                        >
                          {t("actionCenter.inbox.rail.grouped")}
                        </button>
                        <button
                          type="button"
                          className={`${styles.fsegBtn} ${groupMode === "flat" ? styles.fsegOn : ""}`}
                          onClick={() => setGroupMode("flat")}
                        >
                          {t("actionCenter.inbox.rail.flat")}
                        </button>
                      </div>
                    </div>
                  )}
                  {inboxHeader}
                  <div className={styles.listScroll}>
                    {railSel === "all" && groupMode === "role" ? (
                      detailSections.length === 0 ? (
                        <div className={styles.empty}>
                          {query ? t("actionCenter.inbox.emptySearch") : t("actionCenter.inbox.emptyClear")}
                        </div>
                      ) : (
                        detailSections.map((sec) => (
                          <React.Fragment key={sec.key}>
                            <div className={styles.sectionHeader}>
                              {(() => {
                                // Same avatar approach as the rail: geo → flag,
                                // org → logo, else the pillar monogram (DEV-1368).
                                if (sec.key.startsWith("community:")) {
                                  const guid = sec.key.slice("community:".length).toUpperCase();
                                  const flag = flagFor(guid);
                                  if (flag)
                                    return (
                                      <span className={styles.railAvatar} aria-hidden="true">
                                        <span style={{ fontSize: "15px" }}>{flag}</span>
                                      </span>
                                    );
                                  const logoRaw = resolveEffectiveLogoUrl(
                                    guid,
                                    communities || [],
                                    (relationships || []) as any,
                                  );
                                  if (logoRaw)
                                    return (
                                      <span
                                        className={styles.railAvatar}
                                        aria-hidden="true"
                                        style={{ overflow: "hidden" }}
                                      >
                                        <img
                                          src={mediaSrc(logoRaw)}
                                          alt=""
                                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                      </span>
                                    );
                                }
                                return railAvatar(
                                  sec.label,
                                  sec.pillar,
                                  sec.key === "inv" ? "ti-mail" : undefined,
                                );
                              })()}
                              <span>{sec.label}</span>
                              <span className={styles.sectionCount}>{sec.items.length}</span>
                            </div>
                            {sec.items.map(renderItemRow)}
                          </React.Fragment>
                        ))
                      )
                    ) : railFilteredItems.length === 0 ? (
                      <div className={styles.empty}>
                        {query ? t("actionCenter.inbox.emptySearch") : t("actionCenter.inbox.emptyClear")}
                      </div>
                    ) : (
                      railFilteredItems.map(renderItemRow)
                    )}
                  </div>
                </>
              )}
            </div>

        {/* Tray (To do only) — still a child of .surface, so its absolute
            positioning is measured against the card exactly as before. The
            .railLayout wrapper that used to close here is gone: the rail left
            the card, so the card has one column again. */}
        {view === "todo" && (
          <ActionItemTray
            item={selected}
            open={selId !== null && view === "todo"}
            busy={busy}
            t={t as any}
            handlers={handlers}
            onClose={() => setSelId(null)}
          />
        )}

        {/* Voice-ladder — community-standards read-and-accept modal. The
            checklist row is the pointer; this is the content (§7.9). On
            accept the security cache invalidates, the row completes, and
            org-scoped posting unlocks. */}
        <CommunityStandardsModal
          open={standardsModalOpen}
          onAccepted={() => {
            setStandardsModalOpen(false);
            setSelId(null);
          }}
          onClose={() => setStandardsModalOpen(false)}
        />

        {/* Org-guidelines read-to-accept (Skip 2026-07-08) — the content
            in-task; Accept performs the versioned ack for the right org.
            The org About page stays the durable home (§7.9). */}
        {guidelinesModalItem && (() => {
          const gItem = guidelinesModalItem;
          const isMember = gItem.checklistKey === "org_guidelines_member";
          const guidKey = String(
            gItem.communityGuid || invitedCommunityGuid || "",
          ).toUpperCase();
          const gl = isMember ? orgGuidelinesMap.get(guidKey) : guidelines;
          const orgName =
            (isMember ? gItem.source : communityMap.get(guidKey)) ||
            gItem.source ||
            "";
          return (
            <CommunityGuidelinesModal
              open
              orgName={orgName}
              content={gl?.content || ""}
              version={gl?.version ?? null}
              onAccept={async () => {
                if (isMember && gItem.communityGuid) {
                  const orgGuid = gItem.communityGuid;
                  await acknowledgeCommunityGuidelines(orgGuid);
                  // The map hook keys on the UPPER guid; the single
                  // (invited-org) hook may key on the original casing —
                  // invalidate both.
                  invalidateAndRefetch(`communityGuidelines:${orgGuid}`);
                  if (
                    invitedCommunityGuid &&
                    String(invitedCommunityGuid).toUpperCase() === orgGuid.toUpperCase()
                  ) {
                    invalidateAndRefetch(`communityGuidelines:${invitedCommunityGuid}`);
                  }
                  markResolved(gItem, t("actionCenter.inbox.resolved.acknowledged"));
                } else {
                  // §7.9 invited-org checklist row — hook ack writes the
                  // org-scoped audit record (actor + timestamp + version).
                  await acknowledgeGuidelines();
                  setSelId(null);
                }
                setGuidelinesModalItem(null);
              }}
              onClose={() => setGuidelinesModalItem(null)}
            />
          );
        })()}

        {/* In-place 2FA enrollment (2026-07-10) — closing after a confirm
            leaves the task already resolved (security cache invalidated by
            TotpSetup itself). */}
        <TotpSetupModal open={totpModalOpen} onClose={() => setTotpModalOpen(false)} />

        {/* "Read me first" welcome screen — reading it IS the acknowledgment;
            closing records the flag (idempotent) and the row completes. */}
        <WelcomeScreenModal
          open={welcomeModalOpen}
          onClose={() => {
            setWelcomeModalOpen(false);
            setSelId(null);
            recordOnboardingMilestone({ milestoneKey: "read_me_first_ack" }).catch(() => {});
            recordOnboardingMilestone({ milestoneKey: "welcome_screen_seen" }).catch(() => {});
          }}
        />

        {/* Done tray — read-only; threaded rows show their full chronology. */}
        {view === "done" && selId && (() => {
          const s = visibleDone.find((x) => x.id === selId);
          if (!s) return null;
          const threaded = s.members && s.members.length > 1;

          // ── DEV-1450 §3c — content retention + state-aware affordances ──
          // Content rule: a Done item never loses its content. Affordance
          // rule: navigation stays LIVE forever; consumables render as
          // outcome + date; data renders read-only.
          const outcomeLabel = (row: DoneRow): string | null => {
            if (!row.outcome) return null;
            const key = `actionCenter.inbox.doneOutcome.${row.outcome}`;
            const loc = t(key);
            const base = loc === key ? row.outcome : loc;
            // "by you" when the caller performed the terminal act (the
            // verdict actor on the workflow's last history entry).
            const hist = row.workflow?.history || [];
            const lastBy = hist.length > 0 ? String(hist[hist.length - 1]?.by || "") : "";
            const byYou =
              lastBy && lastBy.toUpperCase() === String(currentUserGuid).toUpperCase();
            const withActor = byYou
              ? `${base} ${t("actionCenter.inbox.doneOutcome.byYou")}`
              : base;
            return row.dateLabel && row.dateLabel !== "—"
              ? `${withActor} · ${row.dateLabel}`
              : withActor;
          };
          // Workflow content block: description + request message + LIVE
          // community/project links (navigation is never consumable).
          const workflowContent = (row: DoneRow): React.ReactNode => {
            const w = row.workflow;
            if (!w) return null;
            const payload = (w.payload as any) || {};
            const desc = String(w.description || "").trim();
            const msg = String(payload.custom_message || "").trim();
            const communityGuid =
              payload.community_entity_guid ||
              (w.target_type === "community" ? w.target_guid : null);
            const miniGuid = payload.mini_project_guid || null;
            const projGuid =
              miniGuid || (w.target_type === "project" ? w.target_guid : null);
            return (
              <>
                {desc ? <p style={{ marginBlockStart: 0 }}>{desc}</p> : null}
                {msg ? <p>“{msg}”</p> : null}
                {(communityGuid || projGuid) && (
                  <div className={styles.trayLinks}>
                    {communityGuid && (
                      <button
                        type="button"
                        className={styles.trayInlineLink}
                        onClick={() => navigate(`/communities/${communityGuid}`)}
                      >
                        ↗ {t("actionCenter.inbox.action.openCommunity")}
                      </button>
                    )}
                    {projGuid && (
                      <button
                        type="button"
                        className={styles.trayInlineLink}
                        onClick={() =>
                          navigate(
                            miniGuid
                              ? `/projects/${projGuid}/management`
                              : `/projects/${projGuid}`,
                          )
                        }
                      >
                        ↗ {t("actionCenter.inbox.action.openProject")}
                      </button>
                    )}
                  </div>
                )}
              </>
            );
          };
          const sOutcome = outcomeLabel(s);
          return (
            <aside className={`${styles.tray} ${styles.trayOpen}`}>
              <button type="button" className={styles.trayClose} aria-label={t("actionCenter.inbox.close")} onClick={() => setSelId(null)}>
                {/* DEV-1257-followup. Unicode × — Tabler webfont not loaded; see ActionItemTray. */}
                ×
              </button>
              <div className={styles.typeChip} style={{ display: "inline-block" }}>{t(s.typeKey)}</div>
              <div className={styles.trayTitle}>{s.subject}</div>
              <div className={styles.traySub}>
                {s.source && s.source !== "—" ? `${s.source} · ${s.dateLabel}` : s.dateLabel}
              </div>
              <div className={styles.trayBody}>
                {s.retired && (
                  <p style={{ display: "flex", gap: 6, alignItems: "center", color: "var(--text-muted)", marginBlockStart: 0 }}>
                    <i className="ti ti-info-circle" aria-hidden="true" /> {t("actionCenter.inbox.tray.roleRetired")}
                  </p>
                )}
                {threaded ? (
                  <>
                    {/* Lifecycle chronology keeps each event's content +
                        outcome; the latest member's workflow content renders
                        below so what-was-agreed-to stays readable. */}
                    <p>{t("actionCenter.inbox.tray.threadHistory")}</p>
                    <ol style={{ paddingInlineStart: 18, margin: 0, display: "grid", gap: 6 }}>
                      {s.members!.map((m, i) => (
                        <li key={`${m.id}-${i}`}>
                          {m.subject} · {outcomeLabel(m) || m.dateLabel}
                        </li>
                      ))}
                    </ol>
                    {workflowContent(s.members![s.members!.length - 1])}
                  </>
                ) : s.id === "gs:promotion_notice" ? (
                  // Promotion Notice history (Skip 2026-07-07): the Done tray
                  // keeps the explanation reachable and can re-open the full
                  // Contributor welcome (the Impact Signature tease lives
                  // there) — resolved rows never lose their content (§7.9).
                  <>
                    <p>{t("actionCenter.inbox.gettingStarted.promotion.body")}</p>
                    <div className={styles.trayFooter}>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        style={{ marginInlineStart: "auto" }}
                        onClick={() =>
                          window.dispatchEvent(
                            new CustomEvent("l4e:show-contributor-welcome"),
                          )
                        }
                      >
                        {t("actionCenter.inbox.gettingStarted.promotion.viewWelcome")}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Item content — rhythm duty text, checklist nudge
                        (gs rows), or the milestone nudge fallback. A Done
                        item never collapses to a bare title (§3c). */}
                    {(s.description ||
                      (s.milestone?.nudgeKey ? t(s.milestone.nudgeKey) : null)) && (
                      <p style={{ marginBlockStart: 0 }}>
                        {s.description ||
                          (s.milestone?.nudgeKey ? t(s.milestone.nudgeKey) : null)}
                      </p>
                    )}
                    {/* Checklist rows: navigation stays LIVE forever. The
                        welcome screen re-opens its content; enable_2fa is
                        the locked exception (never re-offer enrollment). */}
                    {s.milestone?.checklistKey === "read_me_first" ? (
                      <div className={styles.trayLinks}>
                        <button
                          type="button"
                          className={styles.trayInlineLink}
                          onClick={() => setWelcomeModalOpen(true)}
                        >
                          ↗ {t("actionCenter.inbox.gettingStarted.readMeFirst.cta")}
                        </button>
                      </div>
                    ) : s.milestone?.ctaRoute && s.milestone?.milestone !== "enable_2fa" ? (
                      <div className={styles.trayLinks}>
                        <button
                          type="button"
                          className={styles.trayInlineLink}
                          onClick={() =>
                            navigate(
                              String(s.milestone!.ctaRoute),
                              s.milestone!.ctaState
                                ? { state: s.milestone!.ctaState }
                                : undefined,
                            )
                          }
                        >
                          ↗{" "}
                          {s.milestone?.ctaLabelKey
                            ? t(String(s.milestone.ctaLabelKey))
                            : t("actionCenter.inbox.action.go")}
                        </button>
                      </div>
                    ) : null}
                    {s.milestone?.communityGuid && (
                      <div className={styles.trayLinks}>
                        <button
                          type="button"
                          className={styles.trayInlineLink}
                          onClick={() =>
                            navigate(`/communities/${s.milestone!.communityGuid}`)
                          }
                        >
                          ↗ {t("actionCenter.inbox.action.openCommunity")}
                        </button>
                      </div>
                    )}
                    {/* Workflow-backed rows keep their full content. */}
                    {workflowContent(s)}
                    {/* Grouped data entries: read-only summary + LIVE
                        project link. (Per-value rendering needs backend
                        enrichment — flagged in the DEV-1450 status.) */}
                    {s.id.startsWith("done-data:") && (
                      <>
                        {s.count != null && s.count > 0 && (
                          <p style={{ marginBlockStart: 0 }}>
                            {t("actionCenter.inbox.tray.dataEntriesLine", {
                              count: s.count,
                              date: s.dateLabel,
                            })}
                          </p>
                        )}
                        {s.projectGuid && (
                          <div className={styles.trayLinks}>
                            <button
                              type="button"
                              className={styles.trayInlineLink}
                              onClick={() => navigate(`/projects/${s.projectGuid}`)}
                            >
                              ↗ {t("actionCenter.inbox.action.openProject")}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    {/* Outcome + date — the disabled-consumable record
                        ("Approved by you · Jul 10"). Falls back to the
                        generic closed/complete line when no outcome. */}
                    <p style={{ color: "var(--text-muted)" }}>
                      {sOutcome ||
                        (s.status === "closed"
                          ? t("actionCenter.inbox.tray.sentClosed")
                          : t("actionCenter.inbox.tray.sentComplete"))}
                    </p>
                  </>
                )}
              </div>
            </aside>
          );
        })()}

        {/* Undo bar */}
        {undo && (
          <div className={styles.undoBar}>
            <i className="ti ti-check" aria-hidden="true" style={{ color: "var(--color-success)" }} />
            <span>{t("actionCenter.inbox.undoMarked", { label: undo.label })}</span>
            <button type="button" className={styles.btn} style={{ paddingBlock: 3 }} onClick={doUndo}>
              {t("actionCenter.inbox.action.undo")}
            </button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className={styles.btn}
              style={{ paddingBlock: 3 }}
              onClick={() => {
                setView("done");
                setUndo(null);
              }}
            >
              {t("actionCenter.inbox.viewDone")}
            </button>
          </div>
        )}
        </div>
      </div>
      <EulaModal
        open={eulaOpen}
        onAccepted={() => setEulaOpen(false)}
        onAbort={() => setEulaOpen(false)}
      />
    </div>
  );
};

export default ActionCenterInbox;
